import React, { useState, useEffect, useRef } from 'react';
import {
  Menu, Search, Phone, ClipboardList, MessageCircle, Bell,
  User, Users, Plus, Wrench, Hash, FileText, Calendar, Printer, LogOut,
  ChevronDown, ChevronRight, X, Activity, FlaskConical, BookOpen, Image as ImageIcon,
  AlertCircle, Edit, ListTree, Star, Eye, Trash2, Mic, AlertTriangle,
  Lock, RefreshCw, MoreHorizontal, ChevronsRight, Stethoscope, Home,
} from 'lucide-react';
import BloodflowMark from '../../Assets/BloodflowLogoNoLetters.png';

const BF_LOGO = BloodflowMark;

/* ============ BloodFlow contextualization note (streams line-by-line) ============ */
function countLines(scn) {
  let n = 2; // banner + meta
  scn.sections.forEach((sec) => {
    n += 1; // header
    if (sec.rows) n += sec.rows.length;
    if (sec.p) n += 1;
    if (sec.list) n += sec.list.items.length;
  });
  n += 1; // foot
  return n;
}

const Caret = () => <span className="bfn-caret" />;
const HTML = ({ html }) => <span dangerouslySetInnerHTML={{ __html: html }} />;

function Line({ idx, revealed, streaming, children }) {
  const shown = idx < revealed;
  return (
    <div className={'bfn-line' + (shown ? ' shown' : ' pending')}>
      {children}
      {idx === revealed - 1 && streaming ? <Caret /> : null}
    </div>
  );
}

export function BloodFlowNote({ scenario, token, instant, compact }) {
  const total = countLines(scenario);
  const [revealed, setRevealed] = useState(instant ? total : 0);
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (instant) { setRevealed(total); setStreaming(false); return; }
    setRevealed(0);
    setStreaming(true);
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setRevealed(i);
      if (i >= total) { clearInterval(id); setStreaming(false); }
    }, 95);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [token]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const sc = el.closest('[data-bf-scroll]');
    if (sc) {
      const heads = el.querySelectorAll('.bfn-caret');
      if (heads.length) {
        const h = heads[heads.length - 1];
        const top = h.getBoundingClientRect().top - sc.getBoundingClientRect().top + sc.scrollTop;
        sc.scrollTo({ top: Math.max(0, top - sc.clientHeight * 0.62), behavior: 'smooth' });
      } else if (!streaming) {
        sc.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }, [revealed, streaming]);

  let li = 0;
  const ban = li++;
  const met = li++;

  return (
    <div className={'bfn' + (compact ? ' bfn-compact' : '')} ref={scrollRef}>
      <Line idx={ban} revealed={revealed} streaming={streaming}>
        <div className="bfn-banner">
          <div className="bfn-banner-l">
            <img className="bfn-logo" src={BF_LOGO} alt="BloodFlow" />
            <div>
              <div className="bfn-banner-title">BloodFlow clinical note · ready for review</div>
              <div className="bfn-banner-sub">{scenario.banner.sub}</div>
            </div>
          </div>
          <span className="bfn-chip">{streaming ? 'Writing…' : 'Physician review'}</span>
        </div>
      </Line>

      <Line idx={met} revealed={revealed} streaming={streaming}>
        <div className="bfn-meta">
          {scenario.meta.map((m, i) => (
            <div key={i}><span className="k">{m.k}</span><span className="v">{m.v}</span></div>
          ))}
        </div>
      </Line>

      <div className="bfn-body">
        {scenario.sections.map((sec, si) => {
          const hIdx = li++;
          return (
            <div className="bfn-sec" key={si}>
              <Line idx={hIdx} revealed={revealed} streaming={streaming}>
                <h6>{sec.h}{sec.ix ? <span className="ix">· {sec.ix}</span> : null}</h6>
              </Line>
              {sec.rows && sec.rows.map((r, ri) => {
                const idx = li++;
                return (
                  <Line idx={idx} revealed={revealed} streaming={streaming} key={ri}>
                    <div className="bfn-row">
                      <span className="lbl">{r.lbl}</span>
                      <span className="val"><HTML html={r.val} /></span>
                    </div>
                  </Line>
                );
              })}
              {sec.p && (() => {
                const idx = li++;
                return (
                  <Line idx={idx} revealed={revealed} streaming={streaming}>
                    <p className="bfn-p"><HTML html={sec.p} /></p>
                  </Line>
                );
              })()}
              {sec.list && (
                <ul className={'bfn-ul ' + sec.list.type}>
                  {sec.list.items.map((it, ii) => {
                    const idx = li++;
                    return (
                      <Line idx={idx} revealed={revealed} streaming={streaming} key={ii}>
                        <li><HTML html={it} /></li>
                      </Line>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}

        <Line idx={li++} revealed={revealed} streaming={streaming}>
          <div className="bfn-foot">
            <span>Decision support · physician retains full authority</span>
            <span className="src">{scenario.sources} sources cited</span>
          </div>
        </Line>
      </div>
    </div>
  );
}

export function EmptyNote({ onWrite, label }) {
  return (
    <div className="bfn-empty">
      <div className="ph-lines">
        <span style={{ width: '92%' }}></span>
        <span style={{ width: '78%' }}></span>
        <span style={{ width: '85%' }}></span>
        <span style={{ width: '40%' }}></span>
      </div>
      <div className="ph-cursor"></div>
      <div className="ph-text">{label || 'Empty clinical note — start typing…'}</div>
      <button className="bfn-empty-cta" onClick={onWrite}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5v14" /></svg>
        Generate with BloodFlow
      </button>
      <div className="ph-hint">Invisible layer · no extra clicks in production</div>
    </div>
  );
}

/* ============ EPIC (EpicCare) ============ */
function EpicShell({ noteSlot }) {
  const ep = {
    initials: 'AM', name: 'Marsh, Amara-ONC', sex: 'Female', age: '36 y.o.', dob: '04/03/1989',
    mrn: '402719', hcn: '053 457 3274', legalGuardian: '—',
    gp: 'Dr N. Gemma', gpRole: 'Oncology',
    mdt: 'Breast Unit MDT', mdtRole: 'Local',
    ref: 'Dr C. Young, MD', refRole: 'Ref Clinician',
    allergies: 'Penicillins',
  };
  return (
    <div className="bf-ep2">
      {/* Red gradient top bar */}
      <div className="bf-ep2-top">
        <div className="bf-ep2-top-l">
          <Menu size={14}/>
          <span className="bf-ep2-logo">Epic</span>
          <span className="bf-ep2-ctx">ST MARY'S ONCOLOGY — Outpatient · MARSH, Amara-ONC</span>
        </div>
        <div className="bf-ep2-top-r">
          <div className="bf-ep2-search"><Search size={12}/><span>Search (Ctrl+Space)</span></div>
          <span>Chart Completion 1</span>
          <Phone size={12}/><span>4</span>
          <ClipboardList size={12}/><span>212</span>
          <MessageCircle size={12}/>
          <Bell size={12}/><span>1</span>
          <span className="bf-ep2-wb">_ □ ✕</span>
        </div>
      </div>
      {/* Secondary action bar */}
      <div className="bf-ep2-acts">
        <div className="bf-ep2-acts-l">
          <span><User size={11}/>Patient Lookup</span>
          <span><Plus size={11}/>Encounter</span>
          <span><Wrench size={11}/>My Tools <ChevronDown size={10}/></span>
          <span><Bell size={11}/>Remind Me</span>
          <span><Hash size={11}/>SlicerDicer</span>
          <span><Phone size={11}/>Telephone Call</span>
          <span><ClipboardList size={11}/>ED Track Board</span>
          <span><FileText size={11}/>Prep for Hospital</span>
          <span><Calendar size={11}/>Schedules <ChevronDown size={10}/></span>
        </div>
        <div className="bf-ep2-acts-r">
          <Printer size={11}/>Print
          <LogOut size={11}/>Log Out
          <div className="bf-ep2-avb">PC</div>
        </div>
      </div>
      {/* Patient tab strip */}
      <div className="bf-ep2-tabstrip">
        <div className="bf-ep2-tsicons">
          <ClipboardList size={12}/><Users size={12}/><ListTree size={12}/>
        </div>
        <div className="bf-ep2-tspat">
          MARSH, Amara-ONC <X size={10}/>
        </div>
        <div className="bf-ep2-tsright">
          <span className="bf-ep2-play">PLAYGROUND</span>
          <span className="bf-ep2-ec">EpicCare</span>
        </div>
      </div>

      {/* Body */}
      <div className="bf-ep2-body">
        {/* Sidebar */}
        <aside className="bf-ep2-side" data-bf-scroll>
          <div className="bf-ep2-pt">
            <div className="bf-ep2-pt-av">{ep.initials}</div>
            <ClipboardList size={14} color="#6b7280"/>
          </div>
          <div className="bf-ep2-pt-info">
            <div className="bf-ep2-pt-nm">{ep.name}</div>
            <div className="bf-ep2-pt-line">{ep.sex}, {ep.age}, {ep.dob}</div>
            <div className="bf-ep2-pt-line">MRN: {ep.mrn}</div>
            <div className="bf-ep2-pt-line">HCN: {ep.hcn}</div>
          </div>
          <div className="bf-ep2-resus">Resuscitation: Assume Full<br/>(no ACP docs)</div>
          <div className="bf-ep2-kv"><span className="k">Isolation:</span> None</div>
          <div className="bf-ep2-care">
            <div className="bf-ep2-care-row"><span className="av-r"><User size={10}/></span><div><div className="nm">{ep.gp}</div><div className="rl">{ep.gpRole}</div></div></div>
            <div className="bf-ep2-care-row"><span className="av-s"><Home size={10}/></span><div><div className="nm">{ep.mdt}</div><div className="rl">{ep.mdtRole}</div></div></div>
            <div className="bf-ep2-care-row"><span className="av-r"><Stethoscope size={10}/></span><div><div className="nm">{ep.ref}</div><div className="rl">{ep.refRole}</div></div></div>
          </div>
          <div className="bf-ep2-kv"><span className="k">Allergies:</span> {ep.allergies}</div>
          <div className="bf-ep2-misc">
            <div><span className="k">Nil By Mouth:</span> None</div>
            <div><span className="k">Diabetes Risk:</span> N/A</div>
            <div><span className="k">Social Det.:</span> None</div>
          </div>
          <div className="bf-ep2-review">
            <div className="hd">09:15 REVIEW</div>
            <div>Follow-up: HER2+ breast Ca</div>
            <div>Cycle 4 — Trastuzumab</div>
            <div>Wt: 64.2 kg &gt;5 days</div>
          </div>
          <div className="bf-ep2-sec">SINCE LAST ONCOLOGY VISIT</div>
          <div className="bf-ep2-sec-v">↓ No visits</div>
          <div className="bf-ep2-sec-v">Micro (1)</div>
          <div className="bf-ep2-sec">PROBLEM LIST (4)</div>
          <div className="bf-ep2-warn">FSA Warning. PEWS: Not on File.</div>
          <div className="bf-ep2-sec">PROGRAMMES</div>
          <div className="bf-ep2-sec-v">No active programmes</div>
          <div className="bf-ep2-sec">ASSOCIATED PATHWAY</div>
          <div className="bf-ep2-sec-v">✓ OP First Seen</div>
        </aside>

        {/* Main */}
        <main className="bf-ep2-main">
          {/* Activity tabs */}
          <div className="bf-ep2-tabs">
            {[
              { n: 'Chart Review', I: ClipboardList },
              { n: 'Growth Chart', I: Activity },
              { n: 'Intake', I: FileText },
              { n: 'Charting', I: ClipboardList, active: true },
              { n: 'Investigations', I: FlaskConical },
              { n: 'Prep for Hospital', I: BookOpen },
              { n: 'Conclusion', I: ClipboardList },
              { n: 'Oncology', I: User },
              { n: 'Image Report', I: ImageIcon },
            ].map((t, i) => (
              <div key={i} className={'bf-ep2-tab' + (t.active ? ' active' : '')}>
                <t.I size={11}/>{t.n}
              </div>
            ))}
            <div className="bf-ep2-tab-more">…</div>
          </div>
          {/* Charting subtabs */}
          <div className="bf-ep2-subtabs">
            {['CHARTING','OurPractice Advisories','Visit Info','Visit Diagnoses','Problem List','Allergies','Med Management','Questionnaires','SmartSets'].map((s,i) => (
              <span key={i} className={'bf-ep2-st' + (s==='Med Management' ? ' on' : '') + (i===0 ? ' bold' : '')}>{s}</span>
            ))}
            <span className="bf-ep2-st-r">Charting <Edit size={9}/></span>
          </div>

          {/* Content */}
          <div className="bf-ep2-content" data-bf-scroll>
            {/* Encounters */}
            <section className="bf-ep2-card">
              <header className="bf-ep2-card-h">
                <span className="bf-ep2-card-t">Encounters</span>
                <span className="bf-ep2-new"><Plus size={10}/>New Encounter (FHIR)</span>
              </header>
              <div className="bf-ep2-enc-h">
                <span>Date</span><span>Provider</span><span>Primary Dx</span>
              </div>
              <div className="bf-ep2-enc-r"><span className="lk">29/01/2026</span><span>Dr C. Young — Oncology</span><span>Cycle 4 review</span></div>
              <div className="bf-ep2-enc-r"><span className="lk">15/12/2025</span><span>Dr N. Gemma — Oncology</span><span>Cardiotoxicity screen</span></div>
              <div className="bf-ep2-enc-r"><span className="lk">02/11/2025</span><span>Breast MDT</span><span>Treatment plan ratified</span></div>
            </section>

            {/* Visit Diagnoses */}
            <section className="bf-ep2-card">
              <header className="bf-ep2-card-hr">
                <AlertCircle size={12} color="#a31a26"/>
                <span className="bf-ep2-card-tr">Visit Diagnoses</span>
                <Edit size={10} color="#a31a26"/>
                <Wrench size={10} color="#1976d2" style={{ marginLeft: 'auto' }}/>
              </header>
              <div className="bf-ep2-dx-tools">
                <div className="bf-ep2-dx-search">Search for diagnosis</div>
                <span className="bf-ep2-btn-g"><Plus size={10}/>Add</span>
                <span className="bf-ep2-btn-w">Common <ChevronDown size={9}/></span>
                <span className="bf-ep2-btn-w">Previous <ChevronDown size={9}/></span>
                <span className="bf-ep2-btn-w">Problems <ChevronDown size={9}/></span>
              </div>
              <div className="bf-ep2-dx-th"><span>P</span><span></span><span>SNOMED CT</span><span>ICD-10-UK</span></div>
              <div className="bf-ep2-dx-row">
                <span className="bf-ep2-dx-pn"><ListTree size={10}/>1.<Star size={10} color="#1976d2"/></span>
                <span className="lk">HER2+ invasive ductal carcinoma, left breast</span>
                <span>HER2-positive ductal carcinoma</span>
                <span>C50.9</span>
              </div>
            </section>

            {/* Problem List */}
            <section className="bf-ep2-card">
              <header className="bf-ep2-card-hr">
                <ClipboardList size={12} color="#a31a26"/>
                <span className="bf-ep2-card-tr">Problem List</span>
                <Edit size={10} color="#a31a26"/>
                <span className="bf-ep2-link" style={{ marginLeft: 'auto' }}><Plus size={9}/>Care Coordination Note</span>
              </header>
              <div className="bf-ep2-pl-th"><span>Diagnosis</span><span>Priority</span></div>
              {[
                { a: 'Oncologic',     d: 'Invasive carcinoma, left breast (NST)', p: 'High' },
                { a: 'Hematologic',   d: 'Chemotherapy-related anemia', p: 'Medium' },
                { a: 'Cardiac',       d: 'Anti-HER2 cardiotoxicity surveillance', p: 'Medium' },
                { a: 'Endocrine',     d: 'Iatrogenic menopause', p: 'Low' },
              ].map((p, i) => (
                <div key={i} className="bf-ep2-pl-r">
                  <div className="bf-ep2-pl-a">{p.a}</div>
                  <div className="bf-ep2-pl-d"><span className="lk">{p.d}</span><span>↕ {p.p}</span></div>
                </div>
              ))}
              <div className="bf-ep2-reviewed">
                <span className="ck">✓</span> Mark as Reviewed
                <span className="mt">Last Reviewed by Dr C. Young, MD on 27/01/2026 at 16:16</span>
              </div>
            </section>

            {/* Allergies */}
            <section className="bf-ep2-card">
              <header className="bf-ep2-card-hr">
                <AlertTriangle size={12} color="#a31a26"/>
                <span className="bf-ep2-card-tr">Allergies/Contraindications</span>
              </header>
              <div className="bf-ep2-dx-th" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr' }}>
                <span>Allergen</span><span>Reaction</span><span>Severity</span><span>Type</span><span>Noted</span>
              </div>
              <div className="bf-ep2-al-r">
                <span className="lk">Penicillins</span>
                <span>Rash</span>
                <span>Low</span>
                <span>Drug Class</span>
                <span>26/07/2024</span>
              </div>
            </section>
          </div>

          <div className="bf-ep2-foot">
            <span className="g"><Plus size={11}/>ADD ORDER</span>
            <MoreHorizontal size={11}/>
            <span className="g"><Plus size={11}/>ADD DX (1)</span>
            <span className="r"><Printer size={11}/>PRINT</span>
          </div>
        </main>

        {/* Notes panel */}
        <aside className="bf-ep2-notes">
          <div className="bf-ep2-notes-h">
            <ClipboardList size={11} color="#a31a26"/>
            <span>Notes</span>
            <span className="tv">This Visit</span>
            <Wrench size={9} color="#1976d2" style={{ marginLeft: 'auto' }}/>
          </div>
          <div className="bf-ep2-notes-act">
            <span className="link"><Plus size={9}/>Create Note</span>
            <span>Standard <em>1</em></span>
            <span>Simple <em>2</em></span>
          </div>
          <div className="bf-ep2-notes-act">Procedure <em>3</em></div>
          <div className="bf-ep2-notes-mh">
            <span>My Note</span>
            <span className="ic"><MoreHorizontal size={9}/><Lock size={9}/><Wrench size={9}/></span>
          </div>
          <div className="bf-ep2-notes-meta">Progress · Oncology · 13/02/2026 11:23 <ChevronDown size={9}/></div>
          <div className="bf-ep2-notes-meta">Summary: <span className="d">clinic review</span></div>
          <div className="bf-ep2-notes-tools">
            <Star size={10}/><b>B</b><Search size={10}/><RefreshCw size={10}/><Edit size={10}/><Plus size={10} color="#a31a26"/>
          </div>
          <div className="bf-ep2-notes-st">
            Insert SmartText <ChevronsRight size={9}/><ChevronsRight size={9}/><Mic size={9}/><X size={9}/>
          </div>
          <div className="bf-ep2-notes-body" data-bf-scroll>
            {noteSlot}
          </div>
        </aside>
      </div>

      <style>{BF_EP2_CSS}</style>
    </div>
  );
}

const BF_EP2_CSS = `
.bf-ep2 { position: absolute; inset: 0; display: flex; flex-direction: column; background: #f4f4f4; color: #1f1f1f; font-family: 'Segoe UI', Arial, sans-serif; font-size: 11px; }
.bf-ep2 * { box-sizing: border-box; }

/* Top red bar */
.bf-ep2-top { display: flex; align-items: center; justify-content: space-between; padding: 5px 10px; color: #fff; background: linear-gradient(180deg,#b81e2c 0%,#8c1721 100%); flex: 0 0 auto; }
.bf-ep2-top-l { display: flex; align-items: center; gap: 10px; }
.bf-ep2-logo { font-family: Georgia, serif; font-style: italic; font-weight: 700; font-size: 14px; }
.bf-ep2-ctx { font-size: 10.5px; opacity: 0.95; }
.bf-ep2-top-r { display: flex; align-items: center; gap: 9px; font-size: 10.5px; }
.bf-ep2-search { display: flex; align-items: center; gap: 5px; background: rgba(255,255,255,0.14); padding: 2px 8px; border-radius: 3px; }
.bf-ep2-wb { opacity: 0.9; letter-spacing: 2px; }

/* Action bar */
.bf-ep2-acts { display: flex; align-items: center; justify-content: space-between; padding: 4px 10px; color: #fff; background: linear-gradient(180deg,#cc2435 0%,#a31a26 100%); font-size: 10.5px; flex: 0 0 auto; }
.bf-ep2-acts-l { display: flex; align-items: center; gap: 14px; }
.bf-ep2-acts-l > span { display: inline-flex; align-items: center; gap: 4px; }
.bf-ep2-acts-r { display: flex; align-items: center; gap: 8px; }
.bf-ep2-avb { width: 20px; height: 20px; border-radius: 50%; background: rgba(255,255,255,0.22); display: grid; place-items: center; font-size: 9px; font-weight: 700; }

/* Patient tab strip */
.bf-ep2-tabstrip { display: flex; align-items: stretch; background: #f8d7da; border-bottom: 1px solid #a31a26; flex: 0 0 auto; }
.bf-ep2-tsicons { display: flex; align-items: center; gap: 6px; padding: 4px 8px; color: #a31a26; }
.bf-ep2-tspat { display: flex; align-items: center; gap: 8px; padding: 4px 12px; background: #fbe9eb; border-left: 1px solid #a31a26; border-right: 1px solid #a31a26; color: #a31a26; font-weight: 600; font-size: 10.5px; }
.bf-ep2-tsright { margin-left: auto; display: flex; align-items: center; gap: 8px; padding: 0 10px; }
.bf-ep2-play { background: #a31a26; color: #fff; padding: 2px 6px; border-radius: 3px; font-size: 9px; font-weight: 700; }
.bf-ep2-ec { color: #a31a26; font-weight: 700; font-size: 11px; }

/* Body */
.bf-ep2-body { flex: 1; display: grid; grid-template-columns: 220px minmax(0,1fr) 260px; min-height: 0; overflow: hidden; }

/* Sidebar */
.bf-ep2-side { background: #fff; border-right: 1px solid #d1d5db; padding: 10px 12px; overflow-y: auto; font-size: 10.5px; }
.bf-ep2-pt { display: flex; align-items: center; gap: 10px; }
.bf-ep2-pt-av { width: 44px; height: 44px; border-radius: 50%; background: #f4b9a3; color: #7a3522; font-weight: 700; display: grid; place-items: center; font-size: 14px; }
.bf-ep2-pt-info { margin-top: 6px; }
.bf-ep2-pt-nm { font-weight: 700; font-size: 12px; color: #1f1f1f; }
.bf-ep2-pt-line { font-size: 10px; color: #4b5563; }
.bf-ep2-resus { background: #cfe2f3; color: #1f3a5f; border-radius: 3px; padding: 5px 7px; margin-top: 8px; font-size: 10px; line-height: 1.35; }
.bf-ep2-kv { font-size: 10px; margin-top: 8px; color: #1f1f1f; }
.bf-ep2-kv .k { color: #6b7280; }
.bf-ep2-care { display: flex; flex-direction: column; gap: 5px; margin-top: 8px; }
.bf-ep2-care-row { display: flex; align-items: center; gap: 7px; }
.bf-ep2-care-row .av-r, .bf-ep2-care-row .av-s { width: 22px; height: 22px; background: #e5e7eb; display: grid; place-items: center; color: #4b5563; flex: 0 0 auto; }
.bf-ep2-care-row .av-r { border-radius: 50%; }
.bf-ep2-care-row .av-s { border-radius: 4px; }
.bf-ep2-care-row .nm { font-weight: 600; font-size: 10.5px; color: #1f1f1f; }
.bf-ep2-care-row .rl { font-size: 9.5px; color: #6b7280; }
.bf-ep2-misc { font-size: 10px; margin-top: 8px; display: flex; flex-direction: column; gap: 1px; color: #1f1f1f; }
.bf-ep2-misc .k { color: #6b7280; }
.bf-ep2-review { margin-top: 9px; font-size: 10px; color: #1f1f1f; }
.bf-ep2-review .hd { font-weight: 700; }
.bf-ep2-sec { margin-top: 9px; font-size: 9.5px; text-transform: uppercase; letter-spacing: 0.04em; font-weight: 700; color: #6b7280; }
.bf-ep2-sec-v { font-size: 10px; color: #1f1f1f; }
.bf-ep2-warn { background: #fdebd0; padding: 4px 7px; border-radius: 3px; margin-top: 4px; font-size: 10px; color: #92400e; }

/* Main */
.bf-ep2-main { background: #fff; display: flex; flex-direction: column; min-height: 0; overflow: hidden; }
.bf-ep2-tabs { display: flex; background: #f4f4f4; border-bottom: 1px solid #d1d5db; font-size: 10.5px; flex: 0 0 auto; }
.bf-ep2-tab { display: flex; align-items: center; gap: 5px; padding: 5px 10px; border-right: 1px solid #d1d5db; color: #1f1f1f; }
.bf-ep2-tab svg { color: #1976d2; }
.bf-ep2-tab.active { background: #fff7d6; border-bottom: 2px solid #e9a800; margin-bottom: -1px; font-weight: 600; }
.bf-ep2-tab-more { padding: 5px 10px; margin-left: auto; color: #1976d2; }

.bf-ep2-subtabs { display: flex; align-items: center; gap: 14px; padding: 5px 12px; background: #fff; border-bottom: 1px solid #d1d5db; font-size: 10.5px; color: #1976d2; flex: 0 0 auto; overflow-x: hidden; }
.bf-ep2-st { padding-bottom: 1px; }
.bf-ep2-st.bold { font-weight: 700; text-transform: uppercase; color: #1f1f1f; }
.bf-ep2-st.on { color: #2e7d32; border-bottom: 2px solid #2e7d32; font-weight: 700; }
.bf-ep2-st-r { margin-left: auto; color: #6b7280; font-size: 10px; display: inline-flex; align-items: center; gap: 4px; }

.bf-ep2-content { flex: 1; min-height: 0; overflow-y: auto; padding: 10px; display: flex; flex-direction: column; gap: 10px; }

.bf-ep2-card { background: #fff; border: 1px solid #d1d5db; border-radius: 3px; }
.bf-ep2-card-h { display: flex; align-items: center; justify-content: space-between; padding: 4px 8px; background: #f4f4f4; border-bottom: 1px solid #d1d5db; font-size: 10.5px; color: #4b5563; }
.bf-ep2-card-t { font-weight: 600; }
.bf-ep2-card-hr { display: flex; align-items: center; gap: 6px; padding: 4px 8px; background: #fde7eb; border-bottom: 1px solid #d1d5db; font-size: 11px; }
.bf-ep2-card-tr { font-weight: 600; color: #a31a26; }
.bf-ep2-new { display: inline-flex; align-items: center; gap: 4px; background: #2e7d32; color: #fff; padding: 2px 7px; border-radius: 2px; font-size: 10px; }

.bf-ep2-enc-h { display: grid; grid-template-columns: 1fr 1.4fr 1.4fr; padding: 4px 8px; font-size: 9.5px; color: #4b5563; border-bottom: 1px solid #d1d5db; }
.bf-ep2-enc-r { display: grid; grid-template-columns: 1fr 1.4fr 1.4fr; padding: 4px 8px; font-size: 10.5px; border-bottom: 1px solid #f3f4f6; }
.bf-ep2-enc-r:last-child { border-bottom: none; }
.bf-ep2-enc-r .lk { color: #1976d2; text-decoration: underline; }

.bf-ep2-dx-tools { display: flex; align-items: center; gap: 6px; padding: 6px 8px; font-size: 10px; }
.bf-ep2-dx-search { flex: 1; max-width: 220px; height: 20px; border: 1px solid #d1d5db; padding: 0 7px; display: flex; align-items: center; color: #9ca3af; font-size: 10px; }
.bf-ep2-btn-g { display: inline-flex; align-items: center; gap: 3px; background: #2e7d32; color: #fff; padding: 2px 7px; border-radius: 2px; font-size: 10px; }
.bf-ep2-btn-w { background: #fff; border: 1px solid #d1d5db; padding: 2px 7px; border-radius: 2px; color: #1976d2; font-size: 10px; display: inline-flex; align-items: center; gap: 3px; }

.bf-ep2-dx-th { display: grid; grid-template-columns: 28px 1fr 1fr 80px; padding: 3px 8px; font-size: 9px; color: #6b7280; text-transform: uppercase; border-bottom: 1px solid #f3f4f6; }
.bf-ep2-dx-row { display: grid; grid-template-columns: 28px 1fr 1fr 80px; padding: 5px 8px; font-size: 10.5px; align-items: center; }
.bf-ep2-dx-row .lk, .bf-ep2-pl-d .lk { color: #1976d2; text-decoration: underline; }
.bf-ep2-dx-pn { display: inline-flex; align-items: center; gap: 3px; color: #6b7280; }

.bf-ep2-pl-th { display: grid; grid-template-columns: 1fr 80px; padding: 3px 8px; font-size: 9px; color: #6b7280; text-transform: uppercase; border-bottom: 1px solid #f3f4f6; }
.bf-ep2-pl-r { padding: 5px 8px; border-bottom: 1px solid #f3f4f6; }
.bf-ep2-pl-r:last-of-type { border-bottom: none; }
.bf-ep2-pl-a { font-size: 9.5px; color: #4b5563; margin-bottom: 1px; }
.bf-ep2-pl-d { display: grid; grid-template-columns: 1fr auto; align-items: center; font-size: 10.5px; }
.bf-ep2-pl-d > span:last-child { color: #4b5563; font-size: 10px; }

.bf-ep2-reviewed { padding: 5px 8px; border-top: 1px solid #f3f4f6; font-size: 10px; color: #4b5563; display: flex; align-items: center; gap: 6px; }
.bf-ep2-reviewed .ck { color: #2e7d32; font-weight: 700; }
.bf-ep2-reviewed .mt { color: #6b7280; margin-left: 6px; }

.bf-ep2-al-r { display: grid; grid-template-columns: 2fr 1fr 1fr 1fr 1fr; padding: 5px 8px; font-size: 10.5px; align-items: center; }
.bf-ep2-al-r .lk { color: #1976d2; text-decoration: underline; }

.bf-ep2-foot { border-top: 1px solid #d1d5db; padding: 4px 12px; background: #f4f4f4; display: flex; align-items: center; gap: 14px; font-size: 10.5px; flex: 0 0 auto; }
.bf-ep2-foot .g { color: #2e7d32; display: inline-flex; align-items: center; gap: 4px; }
.bf-ep2-foot .r { margin-left: auto; display: inline-flex; align-items: center; gap: 4px; color: #4b5563; }
.bf-ep2-link { display: inline-flex; align-items: center; gap: 3px; color: #1976d2; font-size: 10px; text-decoration: underline; }

/* Notes panel */
.bf-ep2-notes { background: #fff; border-left: 1px solid #d1d5db; display: flex; flex-direction: column; min-height: 0; font-size: 10.5px; }
.bf-ep2-notes-h { display: flex; align-items: center; gap: 6px; padding: 4px 8px; background: #fde7eb; border-bottom: 1px solid #d1d5db; }
.bf-ep2-notes-h > span:first-of-type { font-weight: 600; color: #a31a26; }
.bf-ep2-notes-h .tv { color: #1976d2; margin-left: 6px; }
.bf-ep2-notes-act { display: flex; align-items: center; gap: 10px; padding: 4px 8px; border-bottom: 1px solid #d1d5db; color: #1976d2; font-size: 10px; }
.bf-ep2-notes-act .link { display: inline-flex; align-items: center; gap: 3px; }
.bf-ep2-notes-act em { color: #6b7280; font-style: normal; font-size: 9px; margin-left: 2px; }
.bf-ep2-notes-mh { display: flex; align-items: center; justify-content: space-between; padding: 4px 8px; border-bottom: 1px solid #d1d5db; font-weight: 600; }
.bf-ep2-notes-mh .ic { color: #1976d2; display: inline-flex; gap: 4px; }
.bf-ep2-notes-meta { padding: 3px 8px; font-size: 10px; color: #4b5563; }
.bf-ep2-notes-meta .d { color: #4b5563; }
.bf-ep2-notes-tools { display: flex; align-items: center; gap: 7px; padding: 4px 8px; border-top: 1px solid #d1d5db; border-bottom: 1px solid #d1d5db; color: #1976d2; }
.bf-ep2-notes-st { display: flex; align-items: center; gap: 6px; padding: 4px 8px; border-bottom: 1px solid #d1d5db; font-size: 10px; color: #1976d2; }
.bf-ep2-notes-body { flex: 1; min-height: 0; overflow-y: auto; padding: 8px 10px; font-size: 11px; color: #1f1f1f; }

/* Compact BloodFlow note — scoped & specific to Epic Notes panel (260px wide).
   Prefixed with .bf-ep2 to outrank the global .bf-emr-showcase .bfn-* rules. */
.bf-ep2 .bf-ep2-notes-body .bfn { font-size: 9px; }
.bf-ep2 .bf-ep2-notes-body .bfn * { min-width: 0; }
.bf-ep2 .bf-ep2-notes-body .bfn-banner { padding: 6px 8px; border-radius: 4px; gap: 6px; }
.bf-ep2 .bf-ep2-notes-body .bfn-banner-l { gap: 7px; }
.bf-ep2 .bf-ep2-notes-body .bfn-logo { width: 14px; height: 14px; }
.bf-ep2 .bf-ep2-notes-body .bfn-banner-title { font-size: 9px; line-height: 1.2; }
.bf-ep2 .bf-ep2-notes-body .bfn-banner-sub { font-size: 7px; margin-top: 1px; letter-spacing: .03em; }
.bf-ep2 .bf-ep2-notes-body .bfn-chip { display: none; }
.bf-ep2 .bf-ep2-notes-body .bfn-meta { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 5px 8px; padding: 6px 0 7px; margin-top: 6px; }
.bf-ep2 .bf-ep2-notes-body .bfn-meta > div { min-width: 0; overflow: hidden; }
.bf-ep2 .bf-ep2-notes-body .bfn-meta .k { display: block; font-size: 6.5px; letter-spacing: .06em; margin-bottom: 1px; }
.bf-ep2 .bf-ep2-notes-body .bfn-meta .v { display: block; font-size: 8.5px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.bf-ep2 .bf-ep2-notes-body .bfn-body { padding-top: 7px; }
.bf-ep2 .bf-ep2-notes-body .bfn-sec { margin-bottom: 8px; }
.bf-ep2 .bf-ep2-notes-body .bfn-sec h6 { font-size: 7px; letter-spacing: .1em; margin: 0 0 4px; padding-bottom: 3px; gap: 5px; }
.bf-ep2 .bf-ep2-notes-body .bfn-sec h6 .ix { font-size: 6.5px; }
.bf-ep2 .bf-ep2-notes-body .bfn-row { display: grid; grid-template-columns: 64px minmax(0, 1fr); gap: 6px; padding: 1px 0; font-size: 8.5px; align-items: baseline; }
.bf-ep2 .bf-ep2-notes-body .bfn-row .lbl { font-size: 7.5px; letter-spacing: 0; }
.bf-ep2 .bf-ep2-notes-body .bfn-row .val { line-height: 1.3; word-break: break-word; overflow-wrap: anywhere; }
.bf-ep2 .bf-ep2-notes-body .bfn-p { font-size: 9px; line-height: 1.4; margin: 0; word-break: break-word; }
.bf-ep2 .bf-ep2-notes-body .bfn-ul { margin: 2px 0 0; padding: 0; }
.bf-ep2 .bf-ep2-notes-body .bfn-ul li { font-size: 8.5px; line-height: 1.35; padding: 2px 0 2px 10px; word-break: break-word; }
.bf-ep2 .bf-ep2-notes-body .bfn-ul li::before { font-size: 8.5px; }
.bf-ep2 .bf-ep2-notes-body .bf-cite { font-size: 6.5px; padding: 0 3px; margin: 0 1px; vertical-align: 1px; }
.bf-ep2 .bf-ep2-notes-body .bfn-foot { padding: 6px 0 0; margin-top: 3px; gap: 6px; font-size: 6.5px; letter-spacing: .03em; flex-wrap: wrap; }
.bf-ep2 .bf-ep2-notes-body .bfn-caret { height: 0.9em; width: 1.5px; }
`;

/* ============ ORACLE CERNER (PowerChart) — high fidelity ============ */
function CernerShell({ noteSlot }) {
  const cp = {
    name: 'OKAFOR, Rita', mrn: '710002', dob: '01/01/1957', age: '68y', sex: 'F',
    fin: 'INLH4000710002', loc: 'MS4A · 401 · 01', attending: 'Test, Physician — Hospitalist 1',
    weight: '65.9 kg', allergies: 'codeine, penicillin', service: 'MIP Medical Inpatient',
    room: '401-01', admit: '02/09/2026 09:00 EDT',
  };
  const vitals = [
    { n: 'BP',      l: '120/70', p: '132/79', up: false },
    { n: 'Temp °F', l: '98.2',   p: '97.6',   up: true  },
    { n: 'HR',      l: '85',     p: '76',     up: true  },
    { n: 'Resp',    l: '12',     p: '18',     up: false },
    { n: 'SpO₂',    l: '99',     p: '98',     up: false },
    { n: 'Weight',  l: '65.9',   p: '68.0',   up: false },
  ];
  const labs = [
    { n: 'Carbon Dioxide', l: '18',   p: '—',   t: '08/25 09:00' },
    { n: 'Hgb',            l: '14.8', p: '—',   t: '08/25 09:00' },
    { n: 'Creatinine',     l: '1.1',  p: '0.9', t: '08/25 09:00' },
    { n: 'Glucose',        l: '142',  p: '128', t: '08/25 09:00' },
  ];
  return (
    <div className="bf-cn2">
      {/* Title bar */}
      <div className="bf-cn2-title">
        <div className="bf-cn2-title-l">
          <div className="bf-cn2-pcicn">P</div>
          <span>{cp.name} — {cp.mrn} · Opened by Adams, Lindsay Rae RN</span>
        </div>
        <span className="bf-cn2-wb">_ ▢ ✕</span>
      </div>
      {/* Menu bar */}
      <div className="bf-cn2-menu">
        {['Task','Edit','View','Patient','Chart','Links','Navigation','Help'].map(m => (
          <span key={m}><u>{m[0]}</u>{m.slice(1)}</span>
        ))}
      </div>
      {/* Primary toolbar */}
      <div className="bf-cn2-tools">
        {[
          { I: ClipboardList, l: 'CareCompass' },
          { I: Users,         l: 'Patient List' },
          { I: User,          l: 'Staff Assignment' },
          { I: ClipboardList, l: 'Discharge Dashboard' },
          { I: Activity,      l: 'Quality Measures' },
          { I: MessageCircle, l: 'Message Center' },
          { I: BookOpen,      l: 'Clinician Lab Guide' },
        ].map((b, i) => (
          <span key={i} className="bf-cn2-tool"><b.I size={11}/>{b.l}</span>
        ))}
      </div>
      {/* Secondary toolbar */}
      <div className="bf-cn2-tools2">
        {[
          { I: BookOpen,      l: 'DynaMed' },
          { I: BookOpen,      l: 'Micromedex' },
          { I: BookOpen,      l: 'ClinicalKey' },
        ].map((b, i) => (
          <span key={i} className="bf-cn2-tool"><b.I size={11}/>{b.l}</span>
        ))}
        <span className="bf-cn2-sep"></span>
        {[
          { I: X,             l: 'Tear Off' },
          { I: Lock,          l: 'Suspend' },
          { I: Hash,          l: 'Charges' },
          { I: LogOut,        l: 'Exit' },
          { I: Hash,          l: 'Calculator' },
          { I: Plus,          l: 'AdHoc' },
          { I: MessageCircle, l: 'PM Conversation', dd: true },
          { I: Phone,         l: 'Communicate', dd: true },
          { I: FileText,      l: 'Record Request' },
        ].map((b, i) => (
          <span key={i} className="bf-cn2-tool"><b.I size={11}/>{b.l}{b.dd && <ChevronDown size={9}/>}</span>
        ))}
      </div>
      {/* Patient tab */}
      <div className="bf-cn2-ptab">
        <div className="bf-cn2-ptab-on">{cp.name} <X size={10}/></div>
        <div className="bf-cn2-ptab-r">
          <span>⇦ <u>L</u>ist <ChevronDown size={9}/></span>
          <span><RefreshCw size={10}/>Recent <ChevronDown size={9}/></span>
          <span>Name <Search size={10}/></span>
        </div>
      </div>
      {/* Patient banner */}
      <div className="bf-cn2-banner">
        <div className="bf-cn2-banner-nm">{cp.name}</div>
        <div className="bf-cn2-banner-grid">
          <div><b>DOB:</b>{cp.dob}</div>
          <div><b>Age:</b>{cp.age}</div>
          <div><b>Wt:</b>{cp.weight} · <b>Sex:</b>{cp.sex}</div>
          <div><b>MRN:</b>{cp.mrn}</div>
          <div className="bf-cn2-banner-att"><b>Attending:</b>{cp.attending}</div>
        </div>
        <div className="bf-cn2-banner-grid">
          <div><b>Allergies:</b><span className="bf-cn2-allg"> {cp.allergies}</span></div>
          <div><b>Code Status:</b> Full</div>
          <div><b>Loc:</b>{cp.loc}</div>
          <div><b>Admit Dt:</b>{cp.admit}</div>
          <div><b>FIN:</b>{cp.fin}</div>
        </div>
      </div>

      {/* Body */}
      <div className="bf-cn2-body">
        {/* Left menu */}
        <aside className="bf-cn2-side" data-bf-scroll>
          <div className="bf-cn2-side-h">
            <span>Menu</span>
            <Wrench size={9} color="#1e6091"/>
          </div>
          {[
            { l: 'MAR' },
            { l: 'Carenet Nurse Patient Summary', active: true },
            { l: 'Interactive View and I&O' },
            { l: 'Activities and Interventions' },
            { l: 'Orders', add: true },
            { l: 'Medication List', add: true },
            { l: 'Notes' },
            { l: 'Diagnosis & Problems' },
            { l: 'Allergies', add: true },
            { l: 'Histories' },
            { l: 'Results Review' },
            { l: 'Documentation', add: true },
            { l: 'Form Browser' },
            { l: 'Growth Chart' },
            { l: 'MAR Summary' },
            { l: 'Patient Information' },
            { l: 'Plan of Care Summary' },
            { l: 'Reference' },
            { l: 'IRF Summary' },
            { l: 'Inpatient Summary' },
            { l: 'Historical Viewer' },
          ].map((it, i) => (
            <div key={i} className={'bf-cn2-mi' + (it.active ? ' on' : '')}>
              <span>{it.l}</span>
              {it.add && <span className={'add' + (it.active ? ' inv' : '')}>+ Add</span>}
            </div>
          ))}
        </aside>

        {/* Center cards */}
        <main className="bf-cn2-main">
          <div className="bf-cn2-tabbar">
            <div className="bf-cn2-tab on">Inpatient Summary <X size={9}/></div>
            <div className="bf-cn2-tab">Handoff <X size={9}/></div>
            <div className="bf-cn2-tab">Discharge <X size={9}/></div>
            <span className="bf-cn2-tab-plus"><Plus size={11}/></span>
            <div className="bf-cn2-tab-r"><Search size={10}/><MoreHorizontal size={10}/></div>
          </div>
          <div className="bf-cn2-zoom">
            <Search size={9}/><Search size={9}/><Search size={9}/><span>100%</span>
            <ChevronDown size={9}/>
            <span style={{ marginLeft: 'auto' }}>‹  ›  <Home size={9} style={{ verticalAlign: 'middle' }}/></span>
          </div>

          <div className="bf-cn2-cards" data-bf-scroll>
            {/* Documentation — full width, contains the BloodFlow note */}
            <section className="bf-cn2-card" style={{ gridColumn: '1 / -1' }}>
              <header className="bf-cn2-ch gold">
                <span>Documentation — Clinical Note (FlowCore AI)</span>
                <span className="bf-cn2-ch-r"><MoreHorizontal size={9}/><ChevronDown size={9}/></span>
              </header>
              <div className="bf-cn2-cb" data-bf-scroll style={{ maxHeight: 220, overflowY: 'auto' }}>
                {noteSlot}
              </div>
            </section>

            {/* Patient Info */}
            <section className="bf-cn2-card" style={{ gridColumn: 'span 4' }}>
              <header className="bf-cn2-ch purple"><span>Patient Information</span><span className="bf-cn2-ch-r"><MoreHorizontal size={9}/><ChevronDown size={9}/></span></header>
              <div className="bf-cn2-cb bf-cn2-cb-kv">
                <div><span className="k">Chief Complaint:</span><span className="v dim">No results found</span></div>
                <div><span className="k">Reason For Visit:</span><span className="v">Medical</span></div>
                <div><span className="k">Attending:</span><span className="v">{cp.attending}</span></div>
                <div><span className="k">Service:</span><span className="v">{cp.service}</span></div>
                <div><span className="k">Room/Bed:</span><span className="v">{cp.room}</span></div>
                <div><span className="k">Admit Date:</span><span className="v">{cp.admit}</span></div>
                <div><span className="k">Code Status:</span><span className="v err">Not on file</span></div>
              </div>
            </section>

            {/* Vital Signs */}
            <section className="bf-cn2-card" style={{ gridColumn: 'span 5' }}>
              <header className="bf-cn2-ch purple"><span>Vital Signs</span><span className="bf-cn2-ch-r"><MoreHorizontal size={9}/><ChevronDown size={9}/></span></header>
              <div className="bf-cn2-cb">
                <div className="bf-cn2-vh"><span>All Visits: Last 12 months ▾</span></div>
                <div className="bf-cn2-vth"><span></span><span>Latest</span><span>3 hrs</span><span>Previous</span></div>
                {vitals.map((v, i) => (
                  <div key={i} className="bf-cn2-vr">
                    <span className="lk">{v.n}</span>
                    <span className="bold">{v.l}</span>
                    <span className="dim">3 hrs</span>
                    <span className={v.up ? 'err' : ''}>{v.up ? '↑ ' : ''}{v.p}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Documents */}
            <section className="bf-cn2-card" style={{ gridColumn: 'span 3' }}>
              <header className="bf-cn2-ch gold"><span>Documents (3)</span><span className="bf-cn2-ch-r"><MoreHorizontal size={9}/><ChevronDown size={9}/></span></header>
              <div className="bf-cn2-cb">
                <div className="bf-cn2-vh"><span>All Visits: Last 7 days ▾</span></div>
                <div className="bf-cn2-doch"><span>Type</span><span>Author</span><span>Date</span></div>
                <div className="bf-cn2-docr"><span className="lk">FlowCore Clinical Note</span><span>FHIR</span><span>11/02</span></div>
                <div className="bf-cn2-docr"><span className="lk">History and Physical</span><span>Unknown</span><span>08/25</span></div>
                <div className="bf-cn2-docr"><span className="lk">Nursing Note</span><span>LRADAMS</span><span>08/24</span></div>
              </div>
            </section>

            {/* Consolidated Problems */}
            <section className="bf-cn2-card" style={{ gridColumn: 'span 4' }}>
              <header className="bf-cn2-ch purple"><span>Consolidated Problems</span><span className="bf-cn2-ch-r"><MoreHorizontal size={9}/><ChevronDown size={9}/></span></header>
              <div className="bf-cn2-cb">
                <div className="bf-cn2-vh"><span>All Visits · Classification: All</span></div>
                <div className="bf-cn2-doch"><span>Priority</span><span style={{ gridColumn: 'span 2' }}>Problem</span></div>
                <div className="bf-cn2-cat">▾ This Visit (1)</div>
                <div className="bf-cn2-pl"><span></span><span className="lk" style={{ gridColumn: 'span 2' }}>Community-acquired pneumonia <span className="dim">(J18.9)</span></span></div>
                <div className="bf-cn2-cat">▸ Active (2)</div>
              </div>
            </section>

            {/* Labs */}
            <section className="bf-cn2-card" style={{ gridColumn: 'span 5' }}>
              <header className="bf-cn2-ch purple"><span>Labs</span><span className="bf-cn2-ch-r"><MoreHorizontal size={9}/><ChevronDown size={9}/></span></header>
              <div className="bf-cn2-cb">
                <div className="bf-cn2-vh"><span>All Visits: Last 1 month ▾</span></div>
                <div className="bf-cn2-labh"><span></span><span>Latest</span><span>Prev</span></div>
                <div className="bf-cn2-cat">⌃ Labs — Recent ({labs.length})</div>
                {labs.map((l, i) => (
                  <div key={i} className="bf-cn2-labr">
                    <span><span className="lk">{l.n}</span><span className="dim block">{l.t}</span></span>
                    <span className="bold">{l.l}</span>
                    <span>{l.p}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* New Order Entry */}
            <section className="bf-cn2-card" style={{ gridColumn: 'span 3' }}>
              <header className="bf-cn2-ch purple"><span>New Order Entry +</span><span className="bf-cn2-ch-r"><MoreHorizontal size={9}/><ChevronDown size={9}/></span></header>
              <div className="bf-cn2-cb">
                <div className="bf-cn2-orderbtn">
                  <button className="on">Inpatient</button>
                  <button>Prescription</button>
                </div>
                <div className="bf-cn2-info">
                  <AlertCircle size={9} color="#1e6091"/>
                  <span>The system is still processing available health plans. Retry in a few seconds.</span>
                </div>
                <div className="bf-cn2-search">🔍 Search New Order</div>
                <div className="bf-cn2-tritab">
                  <span>Mine</span><span className="on">Public</span><span>Shared</span>
                </div>
                <div className="bf-cn2-cat">▾ Favorites</div>
                <div className="bf-cn2-fav"><Star size={9}/>My Plan Favorites</div>
              </div>
            </section>
          </div>

          {/* Status footer */}
          <div className="bf-cn2-foot">
            <span></span>
            <span>TB720  LRADAMS  February 11, 2026  16:09 EDT</span>
          </div>
        </main>

        {/* Right activity panel */}
        <aside className="bf-cn2-act" data-bf-scroll>
          <div className="bf-cn2-act-h">
            <AlertCircle size={10} color="#1e6091"/>
            <span>40</span>
            <AlertCircle size={10} color="#1e6091"/>
          </div>
          <div className="bf-cn2-act-l">ACTIVITIES</div>
          {[12, 11, 10, 14, 9, 10, 10, 11, 10].map((c, i) => (
            <div key={i} className="bf-cn2-act-r">
              <div className="bar"></div>
              <span className="ct">{c}</span>
              <div className="lbl">PRN/Continuous</div>
            </div>
          ))}
        </aside>
      </div>

      {/* Bottom time bar */}
      <div className="bf-cn2-timebar">
        <div className="bf-cn2-overdue">Overdue</div>
        <div className="bf-cn2-times">
          {['16:00','17:00','18:00','19:00','20:00','21:00','22:00','23:00','00:00','01:00','02:00','03:00'].map((t, i) => (
            <div key={t} className="tslot">
              <span>{t}</span>
              {i < 5 && <div className="fill"></div>}
            </div>
          ))}
        </div>
      </div>

      <style>{BF_CN2_CSS}</style>
    </div>
  );
}

const BF_CN2_CSS = `
.bf-cn2 { position: absolute; inset: 0; display: flex; flex-direction: column; background: #e8edf3; color: #1f2937; font-family: 'Segoe UI', Tahoma, Arial, sans-serif; font-size: 11px; }
.bf-cn2 * { box-sizing: border-box; min-width: 0; }

.bf-cn2-title { display: flex; align-items: center; justify-content: space-between; background: #d8e4ef; border-bottom: 1px solid #94a3b8; padding: 3px 8px; }
.bf-cn2-title-l { display: flex; align-items: center; gap: 6px; }
.bf-cn2-pcicn { width: 14px; height: 14px; background: #1e6091; color: #fff; display: grid; place-items: center; font-size: 9px; font-weight: 700; }
.bf-cn2-wb { color: #475569; letter-spacing: 2px; font-size: 10px; }

.bf-cn2-menu { display: flex; gap: 14px; background: #eef2f7; border-bottom: 1px solid #cbd5e1; padding: 1px 10px; font-size: 11px; }
.bf-cn2-menu u { text-decoration: underline; }

.bf-cn2-tools, .bf-cn2-tools2 { display: flex; align-items: center; gap: 10px; padding: 3px 10px; font-size: 10.5px; flex-wrap: nowrap; overflow: hidden; white-space: nowrap; }
.bf-cn2-tools { background: #eef2f7; border-bottom: 1px solid #cbd5e1; }
.bf-cn2-tools2 { background: #dde7f0; border-bottom: 1px solid #cbd5e1; }
.bf-cn2-tool { display: inline-flex; align-items: center; gap: 3px; color: #1f2937; }
.bf-cn2-tool svg { color: #1e6091; flex: 0 0 auto; }
.bf-cn2-sep { width: 1px; height: 14px; background: #cbd5e1; margin: 0 4px; flex: 0 0 auto; }

.bf-cn2-ptab { display: flex; align-items: center; background: #cfdce8; border-bottom: 1px solid #94a3b8; }
.bf-cn2-ptab-on { display: flex; align-items: center; gap: 5px; padding: 3px 10px; background: #a8c0d5; color: #0b3a5c; font-weight: 600; border-right: 1px solid #94a3b8; font-size: 10.5px; }
.bf-cn2-ptab-r { margin-left: auto; display: flex; align-items: center; gap: 10px; padding-right: 10px; color: #475569; font-size: 10px; }
.bf-cn2-ptab-r > span { display: inline-flex; align-items: center; gap: 3px; }

.bf-cn2-banner { background: #dde7f0; border-bottom: 1px solid #94a3b8; padding: 4px 10px; font-size: 10.5px; }
.bf-cn2-banner-nm { font-weight: 700; font-size: 12px; }
.bf-cn2-banner-grid { display: grid; grid-template-columns: 1fr 1fr 1.4fr 1fr 1.4fr; gap: 10px; margin-top: 3px; }
.bf-cn2-banner-grid > div { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.bf-cn2-banner-grid b { font-weight: 700; margin-right: 3px; }
.bf-cn2-banner-att { grid-column: span 1; }
.bf-cn2-allg { color: #b3261e; font-weight: 600; }

.bf-cn2-body { flex: 1; display: grid; grid-template-columns: 200px minmax(0,1fr) 180px; min-height: 0; overflow: hidden; }

/* Sidebar */
.bf-cn2-side { background: #eef2f7; border-right: 1px solid #cbd5e1; overflow-y: auto; font-size: 10.5px; }
.bf-cn2-side-h { display: flex; align-items: center; justify-content: space-between; padding: 3px 8px; background: #dde7f0; border-bottom: 1px solid #cbd5e1; font-weight: 600; font-size: 10.5px; }
.bf-cn2-mi { display: flex; align-items: center; justify-content: space-between; padding: 2px 8px; font-size: 10.5px; color: #1f2937; }
.bf-cn2-mi.on { background: #1e6091; color: #fff; }
.bf-cn2-mi .add { font-size: 9px; background: #fff; color: #2e7d32; border: 1px solid #2e7d32; padding: 0 3px; border-radius: 2px; }
.bf-cn2-mi .add.inv { background: rgba(255,255,255,0.22); color: #fff; border-color: rgba(255,255,255,0.4); }

/* Main */
.bf-cn2-main { background: #fff; display: flex; flex-direction: column; min-height: 0; }
.bf-cn2-tabbar { display: flex; align-items: stretch; background: #eef2f7; border-bottom: 1px solid #cbd5e1; font-size: 10.5px; flex: 0 0 auto; }
.bf-cn2-tab { display: flex; align-items: center; gap: 5px; padding: 4px 9px; border-right: 1px solid #cbd5e1; color: #1f2937; }
.bf-cn2-tab.on { background: #fff; color: #1e6091; font-weight: 600; }
.bf-cn2-tab-plus { padding: 4px 8px; color: #1e6091; display: grid; place-items: center; }
.bf-cn2-tab-r { margin-left: auto; display: flex; align-items: center; gap: 6px; padding: 4px 10px; color: #475569; }
.bf-cn2-zoom { display: flex; align-items: center; gap: 5px; padding: 2px 10px; font-size: 10px; background: #eef2f7; border-bottom: 1px solid #cbd5e1; color: #1e6091; flex: 0 0 auto; }

.bf-cn2-cards { flex: 1; min-height: 0; overflow-y: auto; padding: 7px; display: grid; grid-template-columns: repeat(12, minmax(0,1fr)); gap: 7px; align-content: start; }
.bf-cn2-card { background: #fff; border: 1px solid #cbd5e1; border-radius: 2px; min-width: 0; }
.bf-cn2-ch { display: flex; align-items: center; justify-content: space-between; padding: 3px 7px; color: #fff; font-size: 10.5px; font-weight: 600; }
.bf-cn2-ch.purple { background: #9b59b6; }
.bf-cn2-ch.gold { background: #f6b73c; }
.bf-cn2-ch.blue { background: #1e6091; }
.bf-cn2-ch-r { display: inline-flex; gap: 4px; }
.bf-cn2-cb { padding: 5px 7px; font-size: 10.5px; }
.bf-cn2-cb .lk { color: #1e6091; text-decoration: underline; }
.bf-cn2-cb .dim { color: #6b7280; font-size: 10px; }
.bf-cn2-cb .err { color: #b3261e; font-weight: 600; }
.bf-cn2-cb .bold { font-weight: 600; }
.bf-cn2-cb .block { display: block; }

.bf-cn2-cb-kv > div { display: grid; grid-template-columns: 110px 1fr; gap: 4px; padding: 1px 0; font-size: 10.5px; }
.bf-cn2-cb-kv .k { color: #475569; }

.bf-cn2-vh { font-size: 9.5px; color: #475569; margin-bottom: 3px; }
.bf-cn2-vth { display: grid; grid-template-columns: 1.5fr 60px 60px 60px; gap: 6px; font-size: 9px; color: #475569; padding-bottom: 2px; border-bottom: 1px solid #f3f4f6; }
.bf-cn2-vr { display: grid; grid-template-columns: 1.5fr 60px 60px 60px; gap: 6px; font-size: 10.5px; align-items: center; padding: 2px 0; border-bottom: 1px solid #f3f4f6; text-align: right; }
.bf-cn2-vr .lk { text-align: left; }

.bf-cn2-doch { display: grid; grid-template-columns: 2fr 1fr 1fr; gap: 5px; font-size: 9px; color: #475569; border-bottom: 1px solid #f3f4f6; padding: 2px 0; }
.bf-cn2-docr { display: grid; grid-template-columns: 2fr 1fr 1fr; gap: 5px; font-size: 10px; padding: 2px 0; }
.bf-cn2-cat { color: #1e6091; font-size: 10.5px; margin-top: 4px; }
.bf-cn2-pl { display: grid; grid-template-columns: 1fr 3fr; gap: 5px; padding: 2px 0; font-size: 10.5px; }

.bf-cn2-labh { display: grid; grid-template-columns: 1.6fr 60px 60px; gap: 5px; font-size: 9px; color: #475569; padding: 2px 0; border-bottom: 1px solid #f3f4f6; }
.bf-cn2-labr { display: grid; grid-template-columns: 1.6fr 60px 60px; gap: 5px; align-items: center; padding: 2px 0; font-size: 10.5px; text-align: right; }
.bf-cn2-labr > span:first-child { text-align: left; }

.bf-cn2-orderbtn { display: flex; }
.bf-cn2-orderbtn button { font-size: 10px; padding: 2px 9px; border: none; background: #cfdce8; color: #1f2937; cursor: default; }
.bf-cn2-orderbtn button.on { background: #1e6091; color: #fff; }
.bf-cn2-info { display: flex; align-items: flex-start; gap: 4px; background: #fff7d6; border: 1px solid #e9a800; padding: 3px 5px; margin-top: 5px; font-size: 9.5px; }
.bf-cn2-search { margin-top: 5px; border: 1px solid #cbd5e1; padding: 2px 5px; font-size: 10px; color: #9ca3af; }
.bf-cn2-tritab { display: grid; grid-template-columns: 1fr 1fr 1fr; margin-top: 5px; border: 1px solid #cbd5e1; }
.bf-cn2-tritab > span { text-align: center; padding: 2px 0; background: #dde7f0; font-size: 10px; border-right: 1px solid #cbd5e1; }
.bf-cn2-tritab > span.on { background: #fff; }
.bf-cn2-fav { display: flex; align-items: center; gap: 3px; color: #1e6091; font-size: 10px; margin-top: 3px; }

.bf-cn2-foot { display: flex; justify-content: space-between; background: #eef2f7; border-top: 1px solid #cbd5e1; padding: 2px 8px; font-size: 9.5px; color: #475569; flex: 0 0 auto; }

/* Activity panel */
.bf-cn2-act { background: #fff; border-left: 1px solid #cbd5e1; overflow-y: auto; }
.bf-cn2-act-h { display: flex; align-items: center; justify-content: space-between; padding: 3px 8px; background: #dde7f0; border-bottom: 1px solid #cbd5e1; font-weight: 600; }
.bf-cn2-act-l { padding: 4px 8px; font-size: 9px; text-transform: uppercase; font-weight: 700; color: #475569; }
.bf-cn2-act-r { padding: 4px 8px; border-bottom: 1px solid #f3f4f6; }
.bf-cn2-act-r .bar { width: 48px; height: 4px; background: #b3261e; border-radius: 1px; display: inline-block; vertical-align: middle; margin-right: 6px; }
.bf-cn2-act-r .ct { font-weight: 700; font-size: 10.5px; }
.bf-cn2-act-r .lbl { font-size: 9px; color: #6b7280; margin-left: 56px; margin-top: -1px; }

/* Time bar */
.bf-cn2-timebar { display: flex; align-items: stretch; background: #fff; border-top: 1px solid #cbd5e1; flex: 0 0 auto; }
.bf-cn2-overdue { background: #b3261e; color: #fff; font-weight: 600; padding: 4px 10px; font-size: 10.5px; }
.bf-cn2-times { flex: 1; display: flex; font-size: 9.5px; color: #475569; }
.bf-cn2-times .tslot { flex: 1; position: relative; padding: 4px 6px; border-right: 1px solid #e5e7eb; }
.bf-cn2-times .fill { position: absolute; left: 0; right: 0; bottom: 0; height: 3px; background: rgba(46,125,50,0.7); }

/* Compact BloodFlow note for Cerner doc card */
.bf-cn2 .bfn { font-size: 10px; }
.bf-cn2 .bfn * { min-width: 0; }
.bf-cn2 .bfn-banner { padding: 7px 9px; border-radius: 3px; gap: 8px; }
.bf-cn2 .bfn-logo { width: 16px; height: 16px; }
.bf-cn2 .bfn-banner-title { font-size: 10.5px; }
.bf-cn2 .bfn-banner-sub { font-size: 8px; }
.bf-cn2 .bfn-chip { display: none; }
.bf-cn2 .bfn-meta { grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 6px 10px; padding: 6px 0; margin-top: 6px; }
.bf-cn2 .bfn-meta > div { min-width: 0; }
.bf-cn2 .bfn-meta .k { font-size: 7px; }
.bf-cn2 .bfn-meta .v { font-size: 9.5px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.bf-cn2 .bfn-sec h6 { font-size: 8px; }
.bf-cn2 .bfn-row { grid-template-columns: 130px minmax(0, 1fr); gap: 10px; padding: 1px 0; font-size: 9.5px; }
.bf-cn2 .bfn-row .lbl { font-size: 8.5px; }
.bf-cn2 .bfn-p { font-size: 10px; line-height: 1.4; }
.bf-cn2 .bfn-ul li { font-size: 9.5px; line-height: 1.4; padding: 2px 0 2px 12px; }
.bf-cn2 .bfn-foot { font-size: 7.5px; padding-top: 6px; }
.bf-cn2 .bf-cite { font-size: 7px; padding: 0 3px; }
`;

/* ============ GLINTT (Mozy Portal) ============ */
function GlinttShell({ noteSlot }) {
  return (
    <div className="gl">
      <div className="gl-crumb">
        <span className="home">⌂ Home</span><span className="sep">›</span>
        <span>Patient List</span><span className="sep">›</span>
        <span className="cur">Joana Durão</span>
        <span className="time">08:05</span>
      </div>
      <div className="gl-banner">
        <div className="gl-avatar">JD</div>
        <div className="gl-id">
          <div className="mid">1923656487</div>
          <div className="nm">Joana Durão</div>
          <div className="dm">20/05/1948 · 77 yrs · Female · 170 cm · 65 kg</div>
        </div>
        <div className="gl-bcell">
          <span className="bl">Allergies</span>
          <span className="bvv gl-pill-red"><span className="n">2</span> Cow's milk · Penicillin</span>
        </div>
        <div className="gl-bcell">
          <span className="bl">Diagnosis</span>
          <span className="bvv">Femoral neck fracture</span>
        </div>
        <div className="gl-bcell">
          <span className="bl">Special care</span>
          <span className="bvv">High falls risk</span>
        </div>
        <div className="gl-nav"><span>‹</span><span>›</span></div>
      </div>
      <div className="gl-body">
        <div className="gl-card gl-note-card">
          <div className="gl-card-h">
            <span className="t">Latest clinical note</span>
            <span className="ic"><span>⤢</span><span>⚙</span></span>
          </div>
          <div className="gl-card-b" data-bf-scroll>
            {noteSlot}
          </div>
        </div>
        <div className="gl-right">
          <div className="gl-rtop">
            <div className="gl-card">
              <div className="gl-card-h"><span className="t">Hemodynamic stability</span><span className="ic"><span>⤢</span><span>⚙</span></span></div>
              <div className="gl-card-b">
                <div className="gl-hemo">
                  <span className="hh">Biomarker</span><span className="hh vv">11:30</span><span className="hh vv">11:41</span><span className="hh vv">11:45</span>
                  <span className="lk">Heart rate (bpm)</span><span className="vv">78</span><span className="vv">80</span><span className="vv">82</span>
                  <span className="lk">Resp. rate (rpm)</span><span className="vv">16</span><span className="vv dim">—</span><span className="vv">16</span>
                  <span className="lk">SpO₂ (%)</span><span className="vv">96</span><span className="vv">97</span><span className="vv">98</span>
                  <span className="lk">Temp (°C)</span><span className="vv">36.8</span><span className="vv dim">—</span><span className="vv">36.8</span>
                  <span className="lk">BP (mmHg)</span><span className="vv">128/74</span><span className="vv dim">—</span><span className="vv dim">—</span>
                </div>
                <div className="gl-news">
                  <span className="lab">NEWS</span>
                  <span className="big">2</span>
                  <span className="tr">decreasing<br />trend · last 6h</span>
                </div>
              </div>
            </div>
            <div className="gl-card">
              <div className="gl-card-h"><span className="t">Therapeutic alerts</span><span className="ic"><span>⤢</span><span>⚙</span></span></div>
              <div className="gl-card-b">
                <div className="gl-alert">
                  <span className="ai">i</span>
                  <div>
                    <div className="at">Active prescription</div>
                    <div className="as">VTE prophylaxis · last given 07:30 · 11/02/2026 · by Dr. J. Cunha</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="gl-rbot">
            <div className="gl-card">
              <div className="gl-card-h"><span className="t">Exams</span><span className="ic"><span>⤢</span></span></div>
              <div className="gl-card-b gl-list">
                <div className="gi"><div className="gd">01/02 · PACS</div><div className="gn">X-ray pelvis — ORIF</div></div>
                <div className="gi"><div className="gd">10/02 · PACS</div><div className="gn">CXR — clear</div></div>
              </div>
            </div>
            <div className="gl-card">
              <div className="gl-card-h"><span className="t">Lab results</span><span className="ic"><span>⤢</span></span></div>
              <div className="gl-card-b">
                <div className="gl-labitem"><span className="lw">⚠</span><span className="ln">Hemoglobin</span><span className="lv">10.6 g/dL</span></div>
                <div className="gl-labitem" style={{ background: '#f4faf5', borderColor: '#d8ecdc' }}><span className="lw" style={{ color: '#3f8f4f' }}>✓</span><span className="ln">Leukocytes</span><span className="lv">8.4 ×10⁹/L</span></div>
                <div className="gl-labitem" style={{ background: '#f4faf5', borderColor: '#d8ecdc' }}><span className="lw" style={{ color: '#3f8f4f' }}>✓</span><span className="ln">CRP</span><span className="lv">9 mg/L</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="gl-input">
        <span className="chip"><span className="av"></span> Joana Durão</span>
        <span className="ph">insert text…</span>
      </div>
    </div>
  );
}

/* ============ INTERSYSTEMS TRAKCARE — high fidelity (dark green) ============ */
function TrakCareShell({ noteSlot }) {
  const actionSections = [
    { h: 'Actions', isActions: true, items: [] },
    { h: 'History', items: ['Add Reason for Encounter','Add Problem','Add Previous Pregnancy','Add Allergy','Add Alert'] },
    { h: 'Current Pregnancy', items: ['Add/Update Current Pregnancy'] },
    { h: 'Current Delivery', items: ['Add/Update Current Delivery'] },
    { h: 'Examinations', items: ['Add Vital Signs','Add Fluid Balance - Input','Add Fluid Balance - Output','Add PCA Observations','Add Epidural Observations','Add Partogram','Add Medical Pictures'] },
    { h: 'Assessments', items: ['Add Edinburgh Postnatal Depression Scale','Add Bishops Score','Add Cardiotocography Review','Add Induction of Labour Assessment'] },
  ];
  return (
    <div className="bf-tc2">
      {/* Dark green top bar */}
      <div className="bf-tc2-top">
        <div className="bf-tc2-top-l">
          <Menu size={13}/>
          <Home size={12}/>
          <span className="back">Back to: Patient Inquiry <ChevronRight size={9}/></span>
        </div>
        <div className="bf-tc2-top-c">GRXX DEV 1076</div>
        <div className="bf-tc2-top-r">
          <Wrench size={11}/>
          <MessageCircle size={11}/>
          <span className="mat"><User size={10}/>Maternity</span>
        </div>
      </div>

      {/* Patient strip with green underline */}
      <div className="bf-tc2-patient">
        <div className="bf-tc2-pid">
          <div className="nm">JENKINS, Aida</div>
          <div className="mrn">RN000000176</div>
        </div>
        <div className="bf-tc2-pinfo">
          <div className="dob">09/09/1990</div>
          <div className="age">28Y 8M 5D - Female</div>
        </div>
        <div className="bf-tc2-spec">Obstetrics/Gynecology</div>
        <div className="bf-tc2-icons">
          <span className="ic"><FileText size={10}/><span className="b">2</span></span>
          <span className="ic"><AlertCircle size={10}/><span className="b">2</span></span>
          <span className="ic"><Activity size={10}/><span className="b">2</span></span>
        </div>
        <span className="bf-tc2-more">⋯</span>
      </div>

      {/* Active Clinical Note header */}
      <div className="bf-tc2-noteh">
        <span className="lbl">Active Clinical Note</span>
        <span className="bf-tc2-more">⥆</span>
      </div>
      <div className="bf-tc2-started">Started 14/05/2019 11:21</div>
      <div className="bf-tc2-tabs">
        {['New','Current','All','My Entries','Search','EPR'].map((t, i) => (
          <span key={t} className={'bf-tc2-tab' + (i === 1 ? ' on' : '')}>{t}</span>
        ))}
      </div>

      {/* Body */}
      <div className="bf-tc2-body">
        {/* Left actions panel */}
        <aside className="bf-tc2-actions" data-bf-scroll>
          {actionSections.map((s, si) => (
            <div key={si} className="bf-tc2-sec">
              <div className={'bf-tc2-sh' + (s.isActions ? ' top' : '')}>
                <ChevronDown size={10}/>
                <span>{s.h}</span>
              </div>
              {s.isActions && (
                <>
                  <div className="bf-tc2-fields">
                    <div className="bf-tc2-field">
                      <label>Extra<br/>Actions</label>
                      <div className="input"><Search size={9}/></div>
                    </div>
                    <div className="bf-tc2-field">
                      <label>Clinical<br/>Pathway</label>
                      <div className="input">Antenatal Care of a Pregnant <Search size={9}/></div>
                    </div>
                  </div>
                </>
              )}
              {s.items.map((it, ii) => (
                <div key={ii} className="bf-tc2-act">{it}</div>
              ))}
            </div>
          ))}
        </aside>

        {/* Main entry pane */}
        <main className="bf-tc2-entry">
          <div className="bf-tc2-entry-h">
            <span className="et">Entry</span>
            <span className="bf-tc2-more">⤢</span>
          </div>
          <div className="bf-tc2-entryline">
            <span className="lock">🔓</span>
            <ChevronDown size={10}/>
            <span className="who"><b>11:22</b> Jennifer CONVERSE (Registered Nurse, Div 1) - Intrapartum Assessment</span>
            <span className="tag">Patient currently pregnant, Gestational diabetes m…</span>
            <span className="bf-tc2-more">⋯</span>
          </div>
          <div className="bf-tc2-entry-b" data-bf-scroll>
            {/* History */}
            <div className="bf-tc2-block">
              <div className="bf-tc2-bh"><ChevronDown size={10}/>History</div>
              <div className="bf-tc2-bsub"><ChevronDown size={10}/>Problems</div>
              <div className="bf-tc2-p">The patient presents with <b>Gestational diabetes mellitus</b> since 25/03/2019 and linked to Pregnancy with AEDD: 03/06/2019. <span className="bf-tc2-more">⋯</span></div>
              <div className="bf-tc2-p">The patient presents with <b>Patient currently pregnant</b> since 27/08/2018 and linked to Pregnancy with AEDD: 03/06/2019. <span className="bf-tc2-more">⋯</span></div>
              <div className="bf-tc2-bsub"><ChevronDown size={10}/>Alerts</div>
              <div className="bf-tc2-p">This patient has an Active Pregnancy / Breastfeeding alert of . This started since less than 1 day ago 14/05/2019. It expired on in 2 weeks. <span className="bf-tc2-more">⋯</span></div>
              <div className="bf-tc2-bsub"><ChevronDown size={10}/>Previous Pregnancies</div>
              <div className="bf-tc2-p">
                <b>Previous Pregnancy Details:</b> - Number of Babies for This Pregnancy: Single pregnancy<br/>
                Labour Method: Induced<br/>
                Antenatal Complications: Abnormal findings on antenatal screening of mother (disorder)
              </div>
            </div>

            {/* BloodFlow Clinical Note block — slots in here */}
            <div className="bf-tc2-block">
              <div className="bf-tc2-bh"><ChevronDown size={10}/>BloodFlow Clinical Note</div>
              <div className="bf-tc2-bfslot">
                {noteSlot}
              </div>
            </div>

            {/* Current Pregnancy */}
            <div className="bf-tc2-block">
              <div className="bf-tc2-bh"><ChevronDown size={10}/>Current Pregnancy</div>
              <div className="bf-tc2-bsub"><ChevronDown size={10}/>Pregnancies</div>
              <div className="bf-tc2-p flagged">
                <b>Current Pregnancy Details:</b> Pregnancy AEDD: 03/06/2019, Status: Open, Date of LMP: 27/08/2018<br/>
                Number of foetuses in this pregnancy: Single pregnancy<br/>
                Delivery Plan Management is Natural<br/>
                Delivery Plan Place is Labor and delivery unit<br/>
                <span className="bf-tc2-warn">The text may not display the latest version of this information.</span>
              </div>
            </div>

            {/* Current Delivery */}
            <div className="bf-tc2-block">
              <div className="bf-tc2-bh"><ChevronDown size={10}/>Current Delivery</div>
              <div className="bf-tc2-bsub"><ChevronDown size={10}/>Deliveries</div>
              <div className="bf-tc2-p">
                <b>Baby Delivery Details:</b> Baby No 1<br/>
                Date/Time of Birth: 14/05/2019 10:00 - Male - Birth Weight: 3500 · Birth Length: 52 · Gestation Weeks: 37 + 1<br/>
                Outcome: Livebirth<br/>
                Delivery Method: Vaginal delivery
              </div>
            </div>
          </div>
        </main>
      </div>

      <style>{BF_TC2_CSS}</style>
    </div>
  );
}

const BF_TC2_CSS = `
.bf-tc2 { position: absolute; inset: 0; display: flex; flex-direction: column; background: #fff; color: #1f2937; font-family: 'Segoe UI', Arial, sans-serif; font-size: 11px; }
.bf-tc2 * { box-sizing: border-box; min-width: 0; }

/* Dark green top bar */
.bf-tc2-top { display: flex; align-items: center; justify-content: space-between; background: #0b6a4e; color: #fff; padding: 4px 10px; flex: 0 0 auto; }
.bf-tc2-top-l { display: flex; align-items: center; gap: 10px; }
.bf-tc2-top-l .back { display: inline-flex; align-items: center; gap: 4px; background: rgba(255,255,255,0.14); padding: 3px 8px; border-radius: 2px; font-size: 10.5px; }
.bf-tc2-top-c { font-size: 11px; font-weight: 600; letter-spacing: 0.04em; }
.bf-tc2-top-r { display: flex; align-items: center; gap: 8px; }
.bf-tc2-top-r .mat { display: inline-flex; align-items: center; gap: 4px; background: rgba(255,255,255,0.14); padding: 2px 9px; border-radius: 2px; font-weight: 600; font-size: 10.5px; }

/* Patient strip */
.bf-tc2-patient { display: flex; align-items: center; gap: 24px; padding: 7px 14px; border-bottom: 2px solid #2f9e6c; background: #fff; flex: 0 0 auto; }
.bf-tc2-pid { line-height: 1.2; }
.bf-tc2-pid .nm { font-weight: 700; font-size: 13px; color: #15402c; }
.bf-tc2-pid .mrn { font-size: 10px; color: #6b7280; }
.bf-tc2-pinfo .dob { font-size: 11px; color: #1f1f1f; }
.bf-tc2-pinfo .age { font-size: 10px; color: #6b7280; }
.bf-tc2-spec { font-size: 11px; color: #1f1f1f; }
.bf-tc2-icons { margin-left: auto; display: flex; gap: 10px; align-items: center; }
.bf-tc2-icons .ic { position: relative; color: #0b6a4e; display: inline-flex; align-items: center; gap: 3px; }
.bf-tc2-icons .b { background: #0b6a4e; color: #fff; font-size: 8px; border-radius: 50%; padding: 0 4px; font-weight: 700; }
.bf-tc2-more { color: #6b7280; }

/* Active Clinical Note header */
.bf-tc2-noteh { display: flex; align-items: center; justify-content: space-between; padding: 5px 14px; background: #fff; border-bottom: 1px solid #d4d8de; flex: 0 0 auto; }
.bf-tc2-noteh .lbl { font-weight: 600; font-size: 11.5px; color: #15402c; }
.bf-tc2-started { padding: 3px 14px; font-size: 10px; color: #6b7280; background: #fff; flex: 0 0 auto; }
.bf-tc2-tabs { display: flex; gap: 0; padding: 0 14px; background: #fff; border-bottom: 1px solid #d4d8de; flex: 0 0 auto; }
.bf-tc2-tab { padding: 5px 14px; font-size: 11px; color: #1f1f1f; cursor: default; }
.bf-tc2-tab.on { background: #0b6a4e; color: #fff; font-weight: 600; }

/* Body */
.bf-tc2-body { flex: 1; display: grid; grid-template-columns: 220px minmax(0,1fr); min-height: 0; overflow: hidden; }

/* Left actions */
.bf-tc2-actions { background: #fff; border-right: 1px solid #d4d8de; overflow-y: auto; padding: 5px 0; }
.bf-tc2-sec { padding: 4px 0; border-bottom: 1px solid #f1f3f5; }
.bf-tc2-sh { display: flex; align-items: center; gap: 5px; padding: 4px 12px; font-weight: 600; font-size: 10.5px; color: #0b6a4e; }
.bf-tc2-sh.top { color: #15402c; }
.bf-tc2-act { padding: 3px 26px; font-size: 10.5px; color: #0b6a4e; cursor: default; text-decoration: underline; text-decoration-color: transparent; }
.bf-tc2-act:hover { text-decoration-color: #0b6a4e; background: #f5faf8; }
.bf-tc2-fields { padding: 4px 12px 6px; display: flex; flex-direction: column; gap: 5px; }
.bf-tc2-field { display: grid; grid-template-columns: 56px 1fr; gap: 6px; align-items: center; font-size: 9.5px; color: #5a6661; }
.bf-tc2-field label { font-size: 9.5px; color: #5a6661; line-height: 1.1; }
.bf-tc2-field .input { display: flex; align-items: center; justify-content: space-between; gap: 4px; background: #fff; border: 1px solid #d4d8de; padding: 2px 5px; font-size: 10px; color: #1f1f1f; }

/* Entry pane */
.bf-tc2-entry { display: flex; flex-direction: column; min-height: 0; background: #fff; overflow: hidden; }
.bf-tc2-entry-h { display: flex; align-items: center; justify-content: space-between; padding: 5px 14px; border-bottom: 1px solid #2f9e6c; background: #fff; flex: 0 0 auto; }
.bf-tc2-entry-h .et { font-weight: 600; color: #1f1f1f; font-size: 11.5px; }
.bf-tc2-entryline { display: flex; align-items: center; gap: 8px; padding: 6px 14px; border-bottom: 1px solid #d4d8de; background: #fff; font-size: 10.5px; flex: 0 0 auto; }
.bf-tc2-entryline .lock { color: #2f9e6c; font-size: 12px; }
.bf-tc2-entryline .who { color: #1f1f1f; }
.bf-tc2-entryline .tag { margin-left: auto; color: #0b6a4e; font-size: 10px; }
.bf-tc2-entry-b { flex: 1; min-height: 0; overflow-y: auto; padding: 8px 14px; background: #fff; }

.bf-tc2-block { margin-bottom: 12px; background: #fff; }
.bf-tc2-bh { display: flex; align-items: center; gap: 6px; padding: 4px 0; font-size: 11.5px; font-weight: 600; color: #0b6a4e; border-bottom: 1px solid #d4d8de; margin-bottom: 6px; }
.bf-tc2-bsub { display: flex; align-items: center; gap: 5px; padding: 3px 0 2px 8px; font-size: 11px; color: #0b6a4e; background: #f5f7f8; }
.bf-tc2-p { font-size: 11px; color: #1f1f1f; line-height: 1.55; padding: 5px 12px; border-bottom: 1px solid #f1f3f5; position: relative; }
.bf-tc2-p .bf-tc2-more { position: absolute; right: 6px; top: 4px; }
.bf-tc2-p b { color: #15402c; font-weight: 700; }
.bf-tc2-p.flagged { border-left: 3px solid #c0392b; padding-left: 10px; }
.bf-tc2-warn { color: #c0392b; font-size: 10.5px; }
.bf-tc2-bfslot { padding: 6px 8px; background: #f5faf8; border: 1px solid #d8e7df; border-radius: 3px; }

/* Compact BloodFlow note */
.bf-tc2 .bfn { font-size: 10.5px; }
.bf-tc2 .bfn * { min-width: 0; }
.bf-tc2 .bfn-banner { padding: 8px 10px; border-radius: 3px; }
.bf-tc2 .bfn-logo { width: 18px; height: 18px; }
.bf-tc2 .bfn-banner-title { font-size: 11px; }
.bf-tc2 .bfn-banner-sub { font-size: 8.5px; }
.bf-tc2 .bfn-chip { display: none; }
.bf-tc2 .bfn-meta { grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 7px 12px; padding: 7px 0; }
.bf-tc2 .bfn-meta .v { font-size: 10.5px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.bf-tc2 .bfn-meta .k { font-size: 8px; }
.bf-tc2 .bfn-sec h6 { font-size: 8.5px; }
.bf-tc2 .bfn-row { grid-template-columns: 140px minmax(0, 1fr); gap: 10px; font-size: 10.5px; }
.bf-tc2 .bfn-row .lbl { font-size: 9px; }
.bf-tc2 .bfn-p { font-size: 10.5px; line-height: 1.45; }
.bf-tc2 .bfn-ul li { font-size: 10px; line-height: 1.4; }
.bf-tc2 .bfn-foot { font-size: 8px; }
.bf-tc2 .bf-cite { font-size: 7.5px; padding: 0 3px; }
`;

/* ============ DEDALUS ORBIS — high fidelity ============ */
function OrbisShell({ noteSlot }) {
  const nav = [
    'Patient Administrative Dashboard',
    'Patient Dashboard',
    'A & E Patient Dashboard',
    'A&E Dashboard V2',
    'MDT Dashboard',
    'Stroke Dashboard',
    'Diabetes Dashboard',
    'Paediatric Growth Chart',
    'Patient Chart',
    'Patient image',
    'Scheduling - Patient',
    'Assessment overview',
    'Cpoe order',
    'Order overview',
    'Nursing overview',
    'Wounds and lines',
    'Lines and drains',
    'Wounds',
    'Diagnoses and problems',
    'Journal and notes',
    'Prescription list',
    'List of nursing interventions',
    'Hospital Prescription',
    'Discharge prescription',
  ];
  return (
    <div className="bf-ob2">
      {/* Top app tabs */}
      <div className="bf-ob2-apptabs">
        <div className="bf-ob2-apptab"><Home size={11}/>ORBIS Overview <span className="pin">📌</span></div>
        <div className="bf-ob2-apptab"><span className="dot blue"></span>GENERAL SURGERY — General Su… <X size={9}/></div>
        <div className="bf-ob2-apptab on"><span className="dot blue"></span>♂ KELLY, Conor | Patient Chart <X size={9}/></div>
        <div className="bf-ob2-app-r">
          <div className="bf-ob2-pid">
            <div>PID 71172</div>
            <div>206075601</div>
            <div className="loc">@100 · ANZSURG</div>
          </div>
          <button className="bf-ob2-create"><Edit size={11}/></button>
          <button className="bf-ob2-create on"><Plus size={11}/>Create</button>
          <button className="bf-ob2-create"><RefreshCw size={11}/></button>
        </div>
      </div>

      {/* Patient banner */}
      <div className="bf-ob2-banner">
        <div className="bf-ob2-pat">
          <span className="bf-ob2-sex">♂</span>
          <div>
            <div className="bf-ob2-nm">KELLY, Conor · 18 Oct 1956 (68 years)</div>
            <div className="bf-ob2-dm">Test Diagnosis | ANZSURG / GSARM2 / GSABED3 · ▣ 206075601 (4 months)</div>
          </div>
        </div>
        <div className="bf-ob2-alert">
          <AlertCircle size={11} color="#b3261e"/>
          <span>1</span>
          <Users size={11} color="#1763d6"/>
        </div>
      </div>

      {/* Body */}
      <div className="bf-ob2-body">
        {/* Left nav */}
        <aside className="bf-ob2-nav" data-bf-scroll>
          <div className="bf-ob2-nav-h">
            <span>Navigation</span>
            <span className="ic"><Wrench size={11}/></span>
            <span className="ic"><ChevronsRight size={11}/></span>
          </div>
          {nav.map((l, i) => (
            <div key={i} className={'bf-ob2-ni' + (l === 'Patient Chart' ? ' on' : '')}>
              <span className="ic"></span>
              <span>{l}</span>
            </div>
          ))}
        </aside>

        {/* Main */}
        <main className="bf-ob2-main">
          {/* Crumbs */}
          <div className="bf-ob2-crumb">
            Patient record / PID 71172 / <b>GENERAL SURGERY / ANZSURG, 206075601 ▾</b> / <b>Patient Chart</b>
            <span className="right">
              <Printer size={11}/>Print
              <BookOpen size={11}/>Encounter history
              <Wrench size={11}/>Settings
              <span>?</span>
            </span>
          </div>

          {/* Chart tabs */}
          <div className="bf-ob2-charts">
            <span className="bf-ob2-cplus"><Plus size={11}/></span>
            <span className="bf-ob2-cn">6</span>
            <span className="bf-ob2-ct"><span className="d" style={{ background: '#7a4f9e' }}></span>Medications <X size={9}/></span>
            <span className="bf-ob2-ct on"><span className="d" style={{ background: '#2c5f9e' }}></span>Overview <X size={9}/></span>
            <span className="bf-ob2-ct"><span className="d" style={{ background: '#e07b00' }}></span>Diabetes <X size={9}/></span>
            <span className="bf-ob2-ct"><span className="d" style={{ background: '#2f7d57' }}></span>Drug groups <X size={9}/></span>
            <span className="bf-ob2-ct"><span className="d" style={{ background: '#1763d6' }}></span>Haemodynamic <X size={9}/></span>
            <span className="bf-ob2-ct red"><AlertTriangle size={9}/>Early Warning Scores <X size={9}/></span>
          </div>

          {/* Time-header row */}
          <div className="bf-ob2-time-h">
            <div className="bf-ob2-time-search"><Search size={9}/></div>
            <div className="bf-ob2-time-grid">
              <span>Sun 27 Oct 2024</span>
              <span>Mon 28 Oct 2024</span>
              <span>Tue 29 Oct 2024 <span className="now">16:01</span></span>
              <span>Wed</span>
              <span className="bf-ob2-time-r"><span className="back">~2 days</span><ChevronRight size={9}/></span>
            </div>
          </div>

          {/* Timeline rows */}
          <div className="bf-ob2-rows" data-bf-scroll>
            {[
              { sec: 'PATIENT INFORMATION', col: '#1763d6', rows: [
                { l: 'Location', c: 1, plus: true },
                { l: 'Length of stay in hospital', c: 1 },
                { l: 'Alerts', cells: [{ t: '⊕ Allergy: Shellfish (substance)', x: 1 }, { t: '⊕ Pacemaker', x: 1 }] },
                { l: 'Allergies', c: 0 },
                { l: 'Sepsis', c: 0 },
                { l: 'Diagnoses', c: 1 },
                { l: 'Height & Weight', c: 2 },
                { l: 'Height, Weight & BMI', cells: [{ t: '⊕', x: 1 }] },
              ]},
              { sec: 'V…', col: '#e07b00', rows: [
                { l: 'Vital parameters', c: 7 },
                { l: 'Lines and drains', c: 1, plus: true },
                { l: 'Arterial catheter' },
                { l: 'Allergien', cells: [{ t: '⊕ Shellfish (substance)', x: 1 }, { t: '⊕ Allergy', x: 1 }, { t: '⊕ Moderate', x: 1 }] },
              ]},
              { sec: 'MEDICATION', col: '#7a4f9e', rows: [
                { l: 'Medication', c: 4, gear: true, plus: true },
                { l: 'paracetamol 500 mg tablet', cells: [{ t: '◯ 1000 mg', x: 1 }, { t: '◯ 1000 mg', x: 1 }, { t: '◯ 1000 mg', x: 1 }, { t: '◯ 1000 mg', x: 1 }] },
                { l: 'metformin hydrochloride 500 mg tablet', cells: [{ t: '◯ 500 mg', x: 1 }, { t: '◯ 500 mg', x: 1 }, { t: '◯ 500 mg', x: 1 }] },
                { l: 'paracetamol 1000mg tablets', cells: [{ t: '⊗', x: 1, red: true }, { t: '⊗', x: 1, red: true }] },
                { l: 'sodium chloride 0.9% injection 1000 mL …', sel: true, cells: [{ t: '◯ 1000 mL (Mor)', x: 1 }] },
                { l: 'SPO2', cells: [{ t: '■ 99', x: 1, green: true }, { t: '■ 99', x: 1, green: true }, { t: '■ 99', x: 1, green: true }, { t: '■ 99', x: 1, green: true }] },
              ]},
              { sec: 'PRESCRIPTIONS', col: '#1763d6', rows: [
                { l: 'Assessments', c: 0, plus: true },
                { l: 'NEWS2', cells: [{ t: '◉ NEWS Single Red Score Scale 2:3 — Low/Medium', x: 2 }, { t: '◉ NEWS Score Scale 2:2 — Low — Recommended 4–6h', x: 1 }] },
                { l: 'Pain Scales', cells: [{ t: '◉ Pain Score: 4 — Moderate pain', x: 1 }] },
                { l: 'Nursing interventions', c: 0 },
                { l: 'Imaging', c: 0 },
                { l: 'Ventilation prescriptions', c: 0, plus: true },
              ]},
              { sec: 'SCORES & SCALES', col: '#9b6a00', rows: [
                { l: 'DAS 28', c: 2 },
                { l: 'Appointments', c: 0 },
                { l: 'Post-op. days', c: 0 },
              ]},
            ].map((g, gi) => (
              <React.Fragment key={gi}>
                <div className="bf-ob2-secline" style={{ color: g.col, borderColor: g.col }}>
                  <span className="lbl" style={{ background: g.col }}>{g.sec}</span>
                </div>
                {g.rows.map((r, ri) => (
                  <div key={ri} className={'bf-ob2-row' + (r.sel ? ' sel' : '')}>
                    <div className="bf-ob2-row-l">
                      <ChevronRight size={9}/>
                      <span>{r.l}</span>
                      {r.plus && <Plus size={9} color="#1763d6"/>}
                      {r.gear && <Wrench size={9}/>}
                      {r.c !== undefined && <span className="cnt">{r.c}</span>}
                    </div>
                    <div className="bf-ob2-row-cells">
                      {r.cells && r.cells.map((c, ci) => (
                        <span key={ci} className={'cell' + (c.red ? ' r' : '') + (c.green ? ' g' : '')} style={{ gridColumn: `span ${c.x || 1}` }}>{c.t}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>

          {/* Note slot (BloodFlow note as fly-out at bottom of chart) */}
          <div className="bf-ob2-note">
            <div className="bf-ob2-noteh">
              <BookOpen size={11}/>
              <span>Journal &amp; notes — BloodFlow Clinical Note</span>
              <span className="meta">New entry · 29/10/2024 16:01</span>
            </div>
            <div className="bf-ob2-noteb" data-bf-scroll>
              {noteSlot}
            </div>
          </div>
        </main>
      </div>

      {/* Bottom help bar */}
      <div className="bf-ob2-help">
        <span>Need to access other patient charts for the patient? Simply click the plus symbol at the top left to view a menu of all available charts tailored to your location and role.</span>
      </div>

      <style>{BF_OB2_CSS}</style>
    </div>
  );
}

const BF_OB2_CSS = `
.bf-ob2 { position: absolute; inset: 0; display: flex; flex-direction: column; background: #eef2f8; color: #1f2b3a; font-family: 'Segoe UI', Arial, sans-serif; font-size: 11px; }
.bf-ob2 * { box-sizing: border-box; min-width: 0; }

/* Top app tab strip */
.bf-ob2-apptabs { display: flex; align-items: stretch; background: #fff; border-bottom: 1px solid #e2e9f2; flex: 0 0 auto; }
.bf-ob2-apptab { display: inline-flex; align-items: center; gap: 5px; padding: 5px 12px; border-right: 1px solid #e2e9f2; font-size: 11px; color: #1f2b3a; }
.bf-ob2-apptab.on { background: #eaf2fb; border-bottom: 2px solid #1763d6; color: #1763d6; font-weight: 600; }
.bf-ob2-apptab .dot { width: 6px; height: 6px; border-radius: 50%; background: #1763d6; flex: 0 0 auto; }
.bf-ob2-apptab .pin { opacity: 0.5; }
.bf-ob2-app-r { margin-left: auto; display: flex; align-items: center; gap: 10px; padding: 0 10px; }
.bf-ob2-pid { font-size: 9.5px; color: #5a6b80; text-align: right; line-height: 1.25; }
.bf-ob2-pid > div:nth-child(2) { color: #1f2b3a; font-weight: 600; font-size: 11px; }
.bf-ob2-pid .loc { color: #5a6b80; font-size: 9.5px; }
.bf-ob2-create { background: #fff; border: 1px solid #d6dde6; padding: 3px 8px; display: inline-flex; align-items: center; gap: 4px; color: #1f2b3a; cursor: default; font-size: 10.5px; border-radius: 2px; }
.bf-ob2-create.on { background: #1763d6; color: #fff; border-color: #1763d6; font-weight: 600; }

/* Patient banner */
.bf-ob2-banner { display: flex; align-items: center; justify-content: space-between; padding: 5px 12px; background: #fff; border-bottom: 1px solid #e2e9f2; flex: 0 0 auto; }
.bf-ob2-pat { display: flex; align-items: center; gap: 8px; }
.bf-ob2-sex { width: 22px; height: 22px; border-radius: 50%; background: #d7e2f2; color: #1763d6; display: grid; place-items: center; font-size: 13px; font-weight: 700; }
.bf-ob2-nm { font-weight: 700; font-size: 12px; color: #16273c; }
.bf-ob2-dm { font-size: 10px; color: #5a6b80; }
.bf-ob2-alert { display: flex; align-items: center; gap: 6px; color: #1f2b3a; font-size: 10.5px; }

/* Body */
.bf-ob2-body { flex: 1; display: grid; grid-template-columns: 200px minmax(0,1fr); min-height: 0; overflow: hidden; }
.bf-ob2-nav { background: #fff; border-right: 1px solid #e2e9f2; overflow-y: auto; }
.bf-ob2-nav-h { display: flex; align-items: center; gap: 5px; padding: 5px 10px; border-bottom: 1px solid #e2e9f2; font-weight: 600; font-size: 11px; }
.bf-ob2-nav-h .ic { margin-left: auto; }
.bf-ob2-ni { display: flex; align-items: center; gap: 7px; padding: 4px 10px; font-size: 10.5px; color: #3a4d66; cursor: default; }
.bf-ob2-ni:hover { background: #f6f9fc; }
.bf-ob2-ni.on { background: #e4edfb; color: #1763d6; font-weight: 600; border-left: 3px solid #1763d6; padding-left: 7px; }
.bf-ob2-ni .ic { width: 14px; height: 14px; border-radius: 2px; background: #dbe6f6; flex: 0 0 auto; }
.bf-ob2-ni.on .ic { background: #1763d6; }

/* Main */
.bf-ob2-main { display: flex; flex-direction: column; min-height: 0; background: #fff; overflow: hidden; }
.bf-ob2-crumb { display: flex; align-items: center; padding: 5px 14px; font-size: 10px; color: #5a6b80; background: #fff; border-bottom: 1px solid #e2e9f2; flex: 0 0 auto; }
.bf-ob2-crumb b { color: #1f2b3a; }
.bf-ob2-crumb .right { margin-left: auto; display: inline-flex; align-items: center; gap: 12px; color: #1f2b3a; }
.bf-ob2-crumb .right svg { color: #5a6b80; }

/* Chart tabs */
.bf-ob2-charts { display: flex; align-items: center; gap: 2px; padding: 4px 10px; border-bottom: 1px solid #e2e9f2; background: #fff; font-size: 10.5px; flex: 0 0 auto; }
.bf-ob2-cplus { background: transparent; border: 1px solid #c0392b; color: #c0392b; padding: 1px 4px; border-radius: 2px; }
.bf-ob2-cn { background: #c0392b; color: #fff; padding: 1px 5px; border-radius: 2px; font-size: 9.5px; font-weight: 700; }
.bf-ob2-ct { display: inline-flex; align-items: center; gap: 5px; padding: 3px 8px; color: #1f2b3a; }
.bf-ob2-ct .d { width: 7px; height: 7px; border-radius: 1px; }
.bf-ob2-ct.on { background: #e4edfb; color: #1763d6; border-bottom: 2px solid #1763d6; font-weight: 600; margin-bottom: -1px; }
.bf-ob2-ct.red { color: #c0392b; }

/* Time grid header */
.bf-ob2-time-h { display: grid; grid-template-columns: 280px minmax(0,1fr); border-bottom: 1px solid #e2e9f2; background: #f6f9fc; font-size: 10.5px; flex: 0 0 auto; }
.bf-ob2-time-search { padding: 5px 10px; border-right: 1px solid #e2e9f2; color: #9aa9bd; }
.bf-ob2-time-grid { display: grid; grid-template-columns: repeat(5, 1fr); position: relative; }
.bf-ob2-time-grid > span { padding: 5px 8px; border-right: 1px solid #e2e9f2; color: #5a6b80; }
.bf-ob2-time-grid .now { background: #c0392b; color: #fff; padding: 0 5px; font-weight: 700; font-size: 9.5px; margin-left: 4px; }
.bf-ob2-time-r { margin-left: auto; display: inline-flex; align-items: center; gap: 4px; }
.bf-ob2-time-r .back { border: 1px solid #c9d6e6; padding: 1px 5px; border-radius: 2px; font-size: 10px; }

/* Rows */
.bf-ob2-rows { flex: 1; min-height: 0; overflow-y: auto; }
.bf-ob2-secline { display: flex; align-items: center; padding: 0; background: #f6f9fc; border-bottom: 1px solid #e2e9f2; }
.bf-ob2-secline .lbl { color: #fff; padding: 2px 8px; font-size: 9px; font-weight: 700; letter-spacing: 0.05em; }
.bf-ob2-row { display: grid; grid-template-columns: 280px minmax(0,1fr); border-bottom: 1px solid #f3f4f6; min-height: 22px; }
.bf-ob2-row.sel { background: #eef5fd; }
.bf-ob2-row-l { display: flex; align-items: center; gap: 5px; padding: 3px 10px; font-size: 10.5px; color: #1f2b3a; border-right: 1px solid #e2e9f2; }
.bf-ob2-row-l .cnt { margin-left: auto; background: #1763d6; color: #fff; width: 16px; height: 16px; border-radius: 50%; display: grid; place-items: center; font-size: 9px; font-weight: 700; }
.bf-ob2-row-cells { display: grid; grid-template-columns: repeat(5, 1fr); align-items: center; }
.bf-ob2-row-cells .cell { padding: 2px 6px; font-size: 10px; color: #1f2b3a; border-right: 1px solid #f3f4f6; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.bf-ob2-row-cells .cell.r { background: #fde7e9; color: #c0392b; }
.bf-ob2-row-cells .cell.g { background: #e6f4ea; color: #2f7d57; }

/* Note slot */
.bf-ob2-note { border-top: 1px solid #e2e9f2; background: #fff; flex: 0 0 auto; max-height: 280px; display: flex; flex-direction: column; }
.bf-ob2-noteh { display: flex; align-items: center; gap: 6px; padding: 5px 12px; background: #eaf2fb; border-bottom: 1px solid #c9d6e6; font-size: 11px; color: #1763d6; font-weight: 600; }
.bf-ob2-noteh .meta { margin-left: auto; color: #5a6b80; font-weight: 400; font-size: 10px; }
.bf-ob2-noteb { padding: 10px 14px; overflow-y: auto; flex: 1; min-height: 0; }

/* Bottom help bar */
.bf-ob2-help { background: #1763d6; color: #fff; padding: 4px 12px; font-size: 10.5px; flex: 0 0 auto; }

/* Compact BloodFlow note */
.bf-ob2 .bfn { font-size: 10.5px; }
.bf-ob2 .bfn * { min-width: 0; }
.bf-ob2 .bfn-banner { padding: 8px 10px; border-radius: 3px; }
.bf-ob2 .bfn-logo { width: 18px; height: 18px; }
.bf-ob2 .bfn-banner-title { font-size: 11px; }
.bf-ob2 .bfn-banner-sub { font-size: 8.5px; }
.bf-ob2 .bfn-chip { display: none; }
.bf-ob2 .bfn-meta { grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 7px 12px; padding: 7px 0; }
.bf-ob2 .bfn-meta .v { font-size: 10.5px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.bf-ob2 .bfn-meta .k { font-size: 8px; }
.bf-ob2 .bfn-sec h6 { font-size: 8.5px; }
.bf-ob2 .bfn-row { grid-template-columns: 140px minmax(0, 1fr); gap: 10px; font-size: 10.5px; }
.bf-ob2 .bfn-row .lbl { font-size: 9px; }
.bf-ob2 .bfn-p { font-size: 10.5px; line-height: 1.45; }
.bf-ob2 .bfn-ul li { font-size: 10px; line-height: 1.4; }
.bf-ob2 .bfn-foot { font-size: 8px; }
.bf-ob2 .bf-cite { font-size: 7.5px; padding: 0 3px; }
`;

/* ============ EMIS WEB — high fidelity (MS Office ribbon UI) ============ */
function EmisShell({ noteSlot }) {
  const rb = (I, color, label, hot) => (
    <button className="bf-em2-rbtn">
      <span className="ri" style={{ background: color }}><I size={14} color="#fff"/></span>
      <span className="rl">{hot ? <><u>{hot}</u>{label.slice(hot.length)}</> : label}</span>
    </button>
  );
  return (
    <div className="bf-em2">
      {/* Top title bar */}
      <div className="bf-em2-titlebar">
        <span className="bf-em2-emis">emis</span>
        <div className="bf-em2-quick">
          {[ClipboardList, Bell, BookOpen, ListTree, Calendar, MessageCircle, FileText, AlertCircle, Search, Hash, Activity].map((I, i) => (
            <span key={i} className="bf-em2-qib"><I size={11} color="#1763d6"/></span>
          ))}
        </div>
        <span className="bf-em2-patname">XXTESTPATIENTAAUG,</span>
        <span className="bf-em2-titletxt">EMIS Web Health Care System — Cofton Medical Centre — 5971</span>
        <span className="bf-em2-wb">— ◻ ✕</span>
      </div>
      {/* Tabs */}
      <div className="bf-em2-tabs">
        {['Summary','Consultations','Medication','Problems','Investigations','Care History','Diary','Documents','Referrals'].map(t => (
          <span key={t} className="bf-em2-tab">{t}</span>
        ))}
        <span className="bf-em2-tab on">New Consultation</span>
      </div>
      {/* Ribbon */}
      <div className="bf-em2-ribbon">
        <div className="bf-em2-grp">
          <div className="rb-row">
            {rb(FileText,      '#2f7d57', 'Save', 'S')}
            <div className="rb-stack">
              <button className="bf-em2-rmini"><X size={10} color="#c0392b"/>Cancel</button>
              <button className="bf-em2-rmini">⌫ Clear</button>
              <button className="bf-em2-rmini"><Lock size={10} color="#9b6a00"/>Confidentiality</button>
              <button className="bf-em2-rmini">↗ Sharing ▾</button>
            </div>
            {rb(Plus,          '#1a5fb4', 'Next Problem')}
          </div>
          <div className="glabel">Consultation</div>
        </div>
        <div className="bf-em2-grp">
          <div className="rb-row">
            {rb(Eye,           '#7a4f9e', 'Online Visibility')}
            {rb(FileText,      '#2f7d57', '23-Jul-2023 09:22 Administration note Cofton Medical Centre')}
          </div>
          <div className="glabel">Date / Consulter / Place</div>
        </div>
        <div className="bf-em2-grp">
          <div className="rb-row">
            {rb(BookOpen,      '#7a4f9e', 'Run Template')}
            {rb(Calendar,      '#2c7fc4', 'Book Appointment')}
            {rb(ClipboardList, '#2f7d57', 'Create Task')}
            {rb(Activity,      '#e07b00', 'Medication Regime Review')}
            {rb(FileText,      '#2566ad', 'Add Fit Note')}
            {rb(Edit,          '#1763d6', 'Spell check', 'S')}
          </div>
          <div className="glabel">Actions</div>
        </div>
        <div className="bf-em2-grp">
          <div className="rb-row">{rb(Printer, '#5a6b80', 'Print')}</div>
          <div className="glabel">Print</div>
        </div>
        <div className="bf-em2-grp">
          <div className="rb-row">{rb(Wrench, '#5a6b80', 'Configure')}</div>
          <div className="glabel">SetUp</div>
        </div>
        <div className="bf-em2-grp" style={{ borderRight: 'none' }}>
          <div className="rb-row">{rb(BookOpen, '#2f7d57', 'Information Leaflets')}</div>
          <div className="glabel">Search</div>
        </div>
      </div>

      {/* Status link bar */}
      <div className="bf-em2-status">
        <span><u>SCR</u> — <b>13</b></span>
        <span><u>Documents</u> — <b>17</b></span>
        <span><u>Registration</u> — <b>435</b> (8)</span>
        <span><u>Lab Reports</u> — <b>11</b></span>
        <span><u>Tasks</u> — <b>4</b></span>
      </div>

      {/* Brown patient banner */}
      <div className="bf-em2-banner">
        <span className="bf-em2-active">⌃ Active <ChevronDown size={9}/></span>
        <span className="bf-em2-pat-nm">XXTESTPATIENTAAUG, David Powell (Mr)</span>
        <span className="bf-em2-pat-cell"><b>Born</b> 29 Apr 1969 (54y)</span>
        <span className="bf-em2-pat-cell"><b>Gender</b> Male</span>
        <span className="bf-em2-pat-cell"><b>EMIS No.</b> 5021903</span>
        <span className="bf-em2-pat-cell"><b>Usual GP</b> Dr BLACKFORD, Kirstie (Dr)</span>
        <span className="bf-em2-pat-r">SCR</span>
      </div>

      {/* Body */}
      <div className="bf-em2-body">
        {/* Left consultation tree */}
        <aside className="bf-em2-tree" data-bf-scroll>
          <div className="bf-em2-tree-h">» Consultation</div>
          {[
            { l: 'Problem' },
            { l: 'History' },
            { l: 'Examination' },
            { l: 'Comment' },
            { l: 'Procedure' },
            { l: 'Medication' },
            { l: 'Test Request', active: true, submenu: true },
            { l: 'Referral' },
            { l: 'Allergy' },
            { l: 'Document' },
            { l: 'Family History' },
          ].map((it, i) => (
            <React.Fragment key={i}>
              <div className={'bf-em2-ti' + (it.active ? ' on' : '')}>
                <span>{it.l}</span>
                {it.submenu && <ChevronRight size={9} color="#1f2937"/>}
              </div>
              {it.submenu && (
                <div className="bf-em2-submenu">
                  <div className="sm-item on">Online Test Request</div>
                  <div className="sm-item">Offline Test Request</div>
                </div>
              )}
            </React.Fragment>
          ))}
        </aside>

        {/* Main center */}
        <main className="bf-em2-center">
          <div className="bf-em2-tabchip">
            <span className="ch on">1. &lt;No Problem&gt; <X size={9}/></span>
          </div>
          <div className="bf-em2-noteline">
            <span className="b">B</span>
            BloodFlow note · written into consultation
          </div>
          <div className="bf-em2-center-b" data-bf-scroll>
            {noteSlot}
          </div>
          <div className="bf-em2-latest">
            <div className="lh">» Latest Contacts</div>
            <div className="lrow">
              <span className="dt">20-Jun-2023 11:30</span>
              <span className="ev">Administration note (Cofton Medical Centre)</span>
              <span className="err">⊗ Error</span>
            </div>
            <div className="lsub">▸ Test Request — <span className="red">New Blood Sciences — Common — Unknown specimen</span></div>
            <div className="lsub">▸ Test Request : Liver Function Test</div>
            <div className="lrow">
              <span className="dt">30-May-2023 08:37</span>
              <span className="ev">Administration note (Cofton Medical Centre)</span>
              <span className="err">⊗ Error</span>
            </div>
            <div className="lsub">▸ Test Request — <span className="red">New Blood Sciences — Common — Unknown specimen</span></div>
            <div className="lsub">▸ Test Request : Lipid Profile</div>
            <div className="lfoot">
              <span>Latest Contacts</span>
              <span>Summary ⊝ ⊕ Detailed</span>
            </div>
          </div>
        </main>

        {/* Right summary */}
        <aside className="bf-em2-sum" data-bf-scroll>
          <div className="bf-em2-sum-h">» Summary</div>
          <div className="bf-em2-sec">
            <div className="bf-em2-sh">Diary</div>
            <div className="bf-em2-shsub">Overdue Tasks</div>
            <div className="bf-em2-si"><span>Test Request Awaiting S…</span><span className="dt">24-May-2023</span></div>
            <div className="bf-em2-si red"><span>Test Request Awaiting S…</span><span className="dt">20-May-2023</span></div>
          </div>
          <div className="bf-em2-sec">
            <div className="bf-em2-sh">Problems</div>
            <div className="bf-em2-shsub">Active Problems</div>
            <div className="bf-em2-si"><span>Administration type</span></div>
          </div>
          <div className="bf-em2-sec">
            <div className="bf-em2-sh">Medication</div>
          </div>
          <div className="bf-em2-alertbox">
            <div className="bf-em2-ah">XXTESTPATIENTAAUG,</div>
            <div className="al"><AlertTriangle size={10} color="#9b6a00"/> No ethnicity recorded</div>
            <div className="al"><AlertTriangle size={10} color="#9b6a00"/> Pneumococcal Vaccination recom…</div>
            <div className="al"><AlertTriangle size={10} color="#9b6a00"/> No ethnicity recorded</div>
            <div className="al"><span className="cir"/> COVID-19: Eligible for vaccination</div>
            <div className="al"><span className="cir"/> No preferred method of contact r…</div>
          </div>
        </aside>
      </div>

      {/* Footer */}
      <div className="bf-em2-foot">
        <span className="nhs">NHS</span>
        <span>Clerical</span>
        <span>POWELL, David (Mr)</span>
        <span>Location: Cofton Medical Centre</span>
      </div>

      <style>{BF_EM2_CSS}</style>
    </div>
  );
}

const BF_EM2_CSS = `
.bf-em2 { position: absolute; inset: 0; display: flex; flex-direction: column; background: #dfe8f2; color: #1f2b3a; font-family: 'Segoe UI', Tahoma, Arial, sans-serif; font-size: 11px; }
.bf-em2 * { box-sizing: border-box; min-width: 0; }

/* Title bar */
.bf-em2-titlebar { display: flex; align-items: center; gap: 8px; padding: 3px 8px; background: linear-gradient(180deg,#fbfdff,#e7eef6); border-bottom: 1px solid #c9d6e6; flex: 0 0 auto; }
.bf-em2-emis { font-weight: 700; color: #1763d6; font-size: 13px; font-style: italic; padding: 1px 6px; background: #fff; border: 1px solid #c9d6e6; border-radius: 2px; }
.bf-em2-quick { display: flex; gap: 1px; }
.bf-em2-qib { padding: 2px 3px; border: 1px solid transparent; }
.bf-em2-qib:hover { background: #fff; border-color: #c9d6e6; }
.bf-em2-patname { margin-left: 60px; color: #1763d6; font-weight: 600; font-size: 11px; }
.bf-em2-titletxt { font-size: 11px; color: #1f2b3a; flex: 1; text-align: center; }
.bf-em2-wb { color: #6b7280; letter-spacing: 2px; }

/* Tabs */
.bf-em2-tabs { display: flex; background: #eaf1fa; border-bottom: 1px solid #c9d6e6; padding: 0 6px; font-size: 11.5px; flex: 0 0 auto; }
.bf-em2-tab { padding: 4px 11px; color: #2b4a6b; border: 1px solid transparent; border-bottom: none; }
.bf-em2-tab.on { background: #fff; border-color: #c9d6e6; color: #c2410c; font-weight: 700; border-radius: 4px 4px 0 0; margin-bottom: -1px; }

/* Ribbon */
.bf-em2-ribbon { display: flex; background: linear-gradient(#fbfdff,#eaf1f8); border-bottom: 1px solid #c9d6e6; padding: 4px 4px 0; flex: 0 0 auto; min-height: 78px; }
.bf-em2-grp { display: flex; flex-direction: column; align-items: stretch; border-right: 1px solid #d6e1ee; padding: 0 4px; min-width: 0; }
.bf-em2-grp .rb-row { display: flex; align-items: flex-start; gap: 2px; padding: 2px 0 4px; }
.bf-em2-grp .glabel { font-size: 9px; color: #5a728f; text-align: center; padding: 2px 0 3px; border-top: 1px solid #d6e1ee; margin-top: auto; }
.bf-em2-rbtn { display: flex; flex-direction: column; align-items: center; gap: 2px; padding: 2px 5px; background: transparent; border: 1px solid transparent; border-radius: 2px; cursor: default; min-width: 0; max-width: 70px; }
.bf-em2-rbtn:hover { background: #fff; border-color: #c9d6e6; }
.bf-em2-rbtn .ri { width: 22px; height: 22px; display: grid; place-items: center; border-radius: 3px; flex: 0 0 auto; }
.bf-em2-rbtn .rl { font-size: 9px; color: #33485f; line-height: 1.1; text-align: center; max-width: 64px; overflow: hidden; text-overflow: ellipsis; }
.bf-em2-rmini { display: flex; align-items: center; gap: 4px; padding: 1px 4px; background: transparent; border: none; font-size: 10px; color: #33485f; cursor: default; }
.bf-em2-rmini:hover { background: #fff; }
.rb-stack { display: flex; flex-direction: column; align-items: flex-start; gap: 1px; padding: 0 2px; }

/* Status link bar */
.bf-em2-status { display: flex; gap: 14px; padding: 2px 12px; background: #d6e1ee; border-bottom: 1px solid #b0c2d6; font-size: 11px; color: #1a5fb4; flex: 0 0 auto; }
.bf-em2-status u { text-decoration: underline; }
.bf-em2-status b { color: #c2410c; }

/* Brown patient banner */
.bf-em2-banner { display: flex; align-items: center; gap: 18px; background: linear-gradient(#7a5836,#5f4327); color: #fff; padding: 5px 12px; font-size: 11.5px; flex: 0 0 auto; }
.bf-em2-active { background: rgba(255,255,255,0.18); padding: 2px 8px; border-radius: 2px; font-weight: 600; display: inline-flex; align-items: center; gap: 4px; }
.bf-em2-pat-nm { font-weight: 700; font-size: 12.5px; }
.bf-em2-pat-cell { color: #fff; }
.bf-em2-pat-cell b { font-weight: 400; opacity: 0.7; margin-right: 4px; }
.bf-em2-pat-r { margin-left: auto; background: rgba(255,255,255,0.18); padding: 2px 9px; border-radius: 2px; font-size: 10.5px; }

/* Body */
.bf-em2-body { flex: 1; display: grid; grid-template-columns: 175px minmax(0,1fr) 240px; min-height: 0; overflow: hidden; }

/* Tree */
.bf-em2-tree { background: #f3f7fb; border-right: 1px solid #c9d6e6; overflow-y: auto; padding: 6px 0; position: relative; }
.bf-em2-tree-h { font-size: 11px; font-weight: 700; color: #2b4a6b; padding: 4px 10px; }
.bf-em2-ti { display: flex; align-items: center; justify-content: space-between; padding: 4px 12px; font-size: 11.5px; color: #33485f; cursor: default; }
.bf-em2-ti:hover { background: #fff7e8; }
.bf-em2-ti.on { background: #fff7e8; color: #c2410c; font-weight: 700; border: 1px solid #f0c878; box-shadow: inset 2px 0 0 #c2410c; }
.bf-em2-submenu { position: relative; margin-left: 110px; margin-top: -30px; z-index: 2; background: #fff; border: 1px solid #c9d6e6; box-shadow: 2px 4px 8px rgba(0,0,0,0.2); width: 170px; }
.bf-em2-submenu .sm-item { padding: 4px 10px; font-size: 11px; cursor: default; }
.bf-em2-submenu .sm-item.on { background: #cfdef3; }
.bf-em2-submenu .sm-item:hover { background: #cfdef3; }

/* Center */
.bf-em2-center { background: #fff; display: flex; flex-direction: column; min-height: 0; border-right: 1px solid #c9d6e6; overflow: hidden; }
.bf-em2-tabchip { display: flex; padding: 4px 8px 0; background: #f3f7fb; border-bottom: 1px solid #c9d6e6; }
.bf-em2-tabchip .ch { display: inline-flex; align-items: center; gap: 5px; padding: 4px 12px; background: #fff; border: 1px solid #c9d6e6; border-bottom: none; font-size: 11px; color: #1f2b3a; }
.bf-em2-tabchip .ch.on { color: #c2410c; font-weight: 600; }
.bf-em2-noteline { display: flex; align-items: center; gap: 7px; padding: 5px 12px; background: #fff7e8; border-bottom: 1px solid #f0d98c; font-size: 11px; color: #9b6a00; flex: 0 0 auto; }
.bf-em2-noteline .b { width: 16px; height: 16px; background: #c2410c; color: #fff; display: grid; place-items: center; font-size: 10px; font-weight: 700; }
.bf-em2-center-b { flex: 1; min-height: 0; overflow-y: auto; padding: 10px 14px; background: #fff; }
.bf-em2-latest { background: #fff; border-top: 1px solid #c9d6e6; padding: 5px 10px; flex: 0 0 auto; max-height: 130px; overflow-y: auto; }
.bf-em2-latest .lh { font-weight: 700; color: #2b4a6b; font-size: 11px; padding-bottom: 3px; border-bottom: 1px solid #e3e9f1; }
.bf-em2-latest .lrow { display: grid; grid-template-columns: 110px minmax(0,1fr) 60px; gap: 8px; padding: 4px 0 2px; font-size: 11px; background: #f3f7fb; border-bottom: 1px solid #e3e9f1; align-items: center; }
.bf-em2-latest .lrow .dt { color: #5a6b80; padding-left: 4px; }
.bf-em2-latest .lrow .ev { color: #1763d6; }
.bf-em2-latest .lrow .err { background: #fde7e9; color: #c0392b; padding: 1px 6px; border-radius: 9px; font-size: 9.5px; text-align: center; }
.bf-em2-latest .lsub { padding: 1px 0 1px 110px; font-size: 11px; color: #1763d6; }
.bf-em2-latest .lsub .red { color: #c0392b; }
.bf-em2-latest .lfoot { display: flex; justify-content: space-between; padding: 4px 0 0; font-size: 10.5px; color: #5a6b80; }

/* Right summary */
.bf-em2-sum { background: #f6f9fc; overflow-y: auto; padding: 6px 8px; }
.bf-em2-sum-h { font-size: 11px; font-weight: 700; color: #2b4a6b; padding: 2px 4px; border-bottom: 1px solid #d6e1ee; margin-bottom: 5px; }
.bf-em2-sec { margin-bottom: 6px; }
.bf-em2-sh { font-size: 11px; font-weight: 700; color: #1a5fb4; border-bottom: 1px solid #d6e1ee; padding: 4px 2px 3px; }
.bf-em2-shsub { font-size: 10.5px; font-weight: 700; color: #1f2b3a; padding: 3px 2px 2px; }
.bf-em2-si { display: grid; grid-template-columns: 1fr auto; gap: 6px; padding: 3px 2px; font-size: 10.5px; color: #1f2b3a; }
.bf-em2-si.red { color: #c0392b; }
.bf-em2-si .dt { color: #5a6b80; font-size: 10px; }
.bf-em2-alertbox { background: #fdeeee; border: 1px solid #f3cccc; border-radius: 2px; padding: 5px 7px; margin-top: 4px; }
.bf-em2-alertbox .bf-em2-ah { font-size: 10.5px; font-weight: 700; color: #2b4a6b; padding-bottom: 3px; border-bottom: 1px solid #f3cccc; margin-bottom: 3px; }
.bf-em2-alertbox .al { display: flex; align-items: center; gap: 5px; font-size: 10px; color: #2b4a6b; padding: 1px 0; }
.bf-em2-alertbox .al .cir { width: 9px; height: 9px; border-radius: 50%; border: 1px solid #94a3b8; background: #fff; flex: 0 0 auto; }

/* Footer */
.bf-em2-foot { display: flex; align-items: center; gap: 16px; padding: 3px 10px; background: linear-gradient(#fbfdff,#e7eef6); border-top: 1px solid #c9d6e6; font-size: 10.5px; color: #5a6b80; flex: 0 0 auto; }
.bf-em2-foot .nhs { background: #005eb8; color: #fff; font-weight: 700; padding: 1px 6px; border-radius: 1px; font-size: 9px; }

/* Compact BloodFlow note */
.bf-em2 .bfn { font-size: 10.5px; }
.bf-em2 .bfn * { min-width: 0; }
.bf-em2 .bfn-banner { padding: 8px 10px; border-radius: 3px; }
.bf-em2 .bfn-logo { width: 18px; height: 18px; }
.bf-em2 .bfn-banner-title { font-size: 11px; }
.bf-em2 .bfn-banner-sub { font-size: 8.5px; }
.bf-em2 .bfn-chip { display: none; }
.bf-em2 .bfn-meta { grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 7px 12px; padding: 7px 0; }
.bf-em2 .bfn-meta .v { font-size: 10.5px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.bf-em2 .bfn-meta .k { font-size: 8px; }
.bf-em2 .bfn-sec h6 { font-size: 8.5px; }
.bf-em2 .bfn-row { grid-template-columns: 140px minmax(0, 1fr); gap: 10px; font-size: 10.5px; }
.bf-em2 .bfn-row .lbl { font-size: 9px; }
.bf-em2 .bfn-p { font-size: 10.5px; line-height: 1.45; }
.bf-em2 .bfn-ul li { font-size: 10px; line-height: 1.4; }
.bf-em2 .bfn-foot { font-size: 8px; }
.bf-em2 .bf-cite { font-size: 7.5px; padding: 0 3px; }
`;

/* ============ TPP SYSTMONE — high fidelity ============ */
function SystmOneShell({ noteSlot }) {
  const sp = { name: 'Mrs Esther Bavington', dob: '12 Mar 1962', age: '64y', sex: 'F',
    address: 'Springfield Cottage, Long Close, Downton, Salisbury SP5 3HG',
    homePhone: '01725 762062', practice: 'Test, Applied, Nurses Stock' };
  const tree = [
    { l: 'Patient Home', active: true, I: Home, c: '#b3261e' },
    { l: 'Major Active Problems (4)', I: ListTree, c: '#1976d2' },
    { l: 'Minor Active Problems (1)', I: ListTree, c: '#1976d2' },
    { l: 'Inactive Problems (1)', I: ListTree, c: '#1976d2' },
    { l: 'MED3 Statements (7)', I: FileText, c: '#1976d2' },
    { l: 'New Journal', I: Edit, c: '#9b59b6' },
    { l: 'Tabbed Journal', I: BookOpen, c: '#9b59b6' },
    { l: 'QOF Timeline', I: Activity, c: '#1976d2' },
    { l: 'Repeat Templates (4)', I: ClipboardList, c: '#1976d2', hi: true },
    { l: 'Medication (4)', I: Activity, c: '#2e7d32', hi: true },
    { l: 'Prescription History (1)', I: Activity, c: '#1976d2', hi: true },
    { l: 'Sensitivities & Allergies', I: AlertTriangle, c: '#f59e0b' },
    { l: 'Communications & Letters (83)', I: MessageCircle, c: '#1976d2' },
    { l: 'Record Attachments (9)', I: FileText, c: '#1976d2' },
    { l: 'Referrals', I: ChevronsRight, c: '#1976d2' },
    { l: 'Questionnaires (2)', I: ClipboardList, c: '#1976d2' },
    { l: 'Safeguarding Information', I: AlertCircle, c: '#1976d2' },
    { l: 'Numeric Results', I: Hash, c: '#1976d2' },
    { l: 'Pathology & Radiology (1)', I: FlaskConical, c: '#1976d2' },
    { l: 'Vaccinations (4)', I: Activity, c: '#2e7d32', hi: true },
  ];
  const alerts = [
    { s: '%', t: 'Consider Seasonal Influenza Vaccination 2025/26 — Age 18-65 at risk', col: '#1976d2' },
    { s: '%', t: 'Eligible for COVID-19 Vaccination — Not had 1st dose', col: '#1976d2' },
    { s: '‼', t: 'Drug Review Required: —', col: '#b3261e' },
    { s: '‼', t: 'End of Life & Palliative Care: —', col: '#b3261e' },
    { s: '⚠', t: 'Future Care Planning / Key information: —', col: '#f59e0b' },
    { s: '!', t: 'On DOAC: —', col: '#b3261e' },
    { s: '★', t: 'IIF HI-01 ? Record health check + action plan as learning disability >14y', col: '#f6b73c' },
    { s: '★', t: 'IIF SMR-03 ? Record UE + CrCl + weight as on DOAC', col: '#f6b73c' },
    { s: '⚠', t: 'Risk of Infection: See problems', col: '#f59e0b' },
    { s: '!', t: 'Steroid Card Issued: Emergency Steroid Card', col: '#b3261e' },
  ];
  return (
    <div className="bf-s12">
      <div className="bf-s12-title">
        <span>SystmOne — Patient Record</span>
        <span className="wb">_ ▢ ✕</span>
      </div>
      <div className="bf-s12-menu">
        {['Patient','Appointments','Reporting','Audit','Setup','Links','Dispensing','Clinical Tools','Workflow','User','System','Help'].map(m => (
          <span key={m}><u>{m[0]}</u>{m.slice(1)}</span>
        ))}
      </div>
      <div className="bf-s12-toolbar">
        <div className="bf-s12-tools">
          {[
            { I: Search,        l: 'Search' },
            { I: ClipboardList, l: 'Task' },
            { I: Trash2,        l: 'Discard' },
            { I: FileText,      l: 'Save' },
            { I: AlertCircle,   l: 'Details' },
            { I: Activity,      l: 'Acute' },
            { I: Phone,         l: 'Recept' },
            { I: MessageCircle, l: 'Comm' },
            { I: Hash,          l: 'CDC' },
            { I: FileText,      l: 'Note' },
          ].map((b, i) => (
            <button key={i} className="bf-s12-tb"><b.I size={16} color="#1976d2"/><span>{b.l}</span></button>
          ))}
        </div>
        <div className="bf-s12-pright">
          <div className="bf-s12-pinfo">
            <AlertCircle size={13} color="#b3261e"/>
            <AlertTriangle size={13} color="#f59e0b"/>
            <div>
              <div className="nm">{sp.name} · {sp.dob} ({sp.age}) {sp.sex}</div>
              <div className="dm">{sp.address}</div>
              <div className="dm">Home (preferred): {sp.homePhone}   {sp.practice}</div>
            </div>
          </div>
          <div className="bf-s12-iconstrip">
            {['C','%','%','🏥','💊','‼','‼','‼','‼','💉','★','⚠','🚩','◔','✓','📞','💧'].map((c, i) => (
              <div key={i} className={'sic ' + (i%3===0 ? 'r' : i%3===1 ? 'y' : 'g')}>{c}</div>
            ))}
          </div>
        </div>
      </div>
      <div className="bf-s12-tabstrip">
        {['Resume Consultation','Next Event','Event Details','Pathology','Drawing','Auto-Consultation','Settings'].map((t, i) => (
          <div key={t} className={'bf-s12-tab' + (i === 0 ? ' on' : '')}>{t}</div>
        ))}
      </div>
      <div className="bf-s12-substrip">
        <span className="on">Clinical</span>
        <span>Administrative</span>
        <span className="hm"><Home size={11}/>Patient Home</span>
      </div>

      <div className="bf-s12-body">
        <aside className="bf-s12-tree" data-bf-scroll>
          {tree.map((n, i) => (
            <div key={i} className={'bf-s12-ti' + (n.active ? ' on' : '') + (n.hi ? ' hi' : '')}>
              <ChevronRight size={9} color="#4b5563"/>
              <n.I size={11} color={n.c}/>
              <span>{n.l}</span>
            </div>
          ))}
        </aside>

        <main className="bf-s12-main" data-bf-scroll>
          {/* Clinical Note card */}
          <div className="bf-s12-card">
            <div className="bf-s12-card-h">
              <BookOpen size={11} color="#9b59b6"/>
              <span>Tabbed Journal — Clinical Note (FlowCore AI)</span>
              <span className="dt">11 Feb 2026 13:32</span>
            </div>
            <div className="bf-s12-card-b" data-bf-scroll>
              {noteSlot}
            </div>
          </div>

          {/* Recalls */}
          <div className="bf-s12-section">
            <div className="bf-s12-sh red">— Recalls</div>
            <div className="bf-s12-sb red">
              <b>18 May 2026</b> Diabetes 12Months Pending
              <span className="lk">Seen</span>
              <span className="lk">Cancel</span>
              <span className="lk">Awaiting Results</span>
              <span className="lk">More</span>
            </div>
          </div>

          {/* Pathology / Radiology */}
          <div className="bf-s12-section">
            <div className="bf-s12-sh blue">— <BookOpen size={10}/>Pathology/Radiology to Archive</div>
            <div className="bf-s12-sb red">
              <b>Abnormal — Need to speak to nurse</b>
              <span className="lk">Inform / Archive</span>
              <span className="lk">More</span>
            </div>
          </div>

          {/* Patient Status Alerts */}
          <div className="bf-s12-section">
            <div className="bf-s12-sh gold"><AlertTriangle size={10}/> — Patient Status Alerts</div>
            <div className="bf-s12-sb">
              {alerts.map((a, i) => (
                <div key={i} className="bf-s12-alert">
                  <span className="sym" style={{ color: a.col }}>{a.s}</span>
                  <span>{a.t} <span className="lk">Action</span> <span className="lk">More</span></span>
                </div>
              ))}
            </div>
          </div>

          {/* Record open elsewhere */}
          <div className="bf-s12-section">
            <div className="bf-s12-sh blue">— <Eye size={10}/>Patient Record Open Elsewhere
              <span className="lk">Check Again</span>
              <span className="lk">Mark record not in use</span>
            </div>
            <div className="bf-s12-sb">Miss Test Ardens was in this patient record when you retrieved the record.</div>
          </div>
        </main>

        <aside className="bf-s12-iconcol">
          <Wrench size={10} color="#4b5563"/>
          <span className="bn">1</span>
          <span className="bn">1</span>
          <span className="bn">18</span>
          <span className="bn r">1</span>
        </aside>
      </div>

      <div className="bf-s12-foot">
        <input className="search" placeholder="Search features"/>
        <div className="counts">
          <span>0 174 1 4 179</span><span>282 0</span><span>0</span><span>0 0 0</span><span>87 0</span>
          <span>13:32</span>
        </div>
      </div>

      <style>{BF_S12_CSS}</style>
    </div>
  );
}

const BF_S12_CSS = `
.bf-s12 { position: absolute; inset: 0; display: flex; flex-direction: column; background: #fff; color: #1f2937; font-family: Arial, 'Segoe UI', sans-serif; font-size: 11px; }
.bf-s12 * { box-sizing: border-box; min-width: 0; }

.bf-s12-title { display: flex; align-items: center; justify-content: space-between; padding: 1px 8px; background: #f1f5f9; border-bottom: 1px solid #cbd5e1; font-size: 10.5px; color: #4b5563; flex: 0 0 auto; }
.bf-s12-title .wb { letter-spacing: 2px; font-size: 10px; }

.bf-s12-menu { display: flex; gap: 14px; padding: 1px 8px; background: #fff; border-bottom: 1px solid #e5e7eb; font-size: 11px; flex: 0 0 auto; }
.bf-s12-menu u { text-decoration: underline; }

.bf-s12-toolbar { display: flex; justify-content: space-between; padding: 4px 8px; background: #fff; border-bottom: 1px solid #e5e7eb; flex: 0 0 auto; }
.bf-s12-tools { display: flex; gap: 3px; }
.bf-s12-tb { display: flex; flex-direction: column; align-items: center; gap: 1px; width: 44px; background: none; border: 1px solid transparent; padding: 2px 0; font-size: 9px; color: #1f2937; cursor: default; }
.bf-s12-tb:hover { background: #f8fafc; border-color: #e5e7eb; }
.bf-s12-pright { display: flex; flex-direction: column; align-items: flex-end; gap: 3px; }
.bf-s12-pinfo { display: flex; align-items: center; gap: 6px; font-size: 10.5px; text-align: right; }
.bf-s12-pinfo .nm { font-weight: 600; font-size: 11px; }
.bf-s12-pinfo .dm { font-size: 10px; color: #4b5563; }
.bf-s12-iconstrip { display: flex; gap: 2px; flex-wrap: wrap; justify-content: flex-end; max-width: 360px; }
.bf-s12-iconstrip .sic { width: 17px; height: 17px; display: grid; place-items: center; font-size: 10px; font-weight: 700; border-radius: 2px; }
.bf-s12-iconstrip .sic.r { background: #fde7e9; color: #b3261e; }
.bf-s12-iconstrip .sic.y { background: #fff7d6; color: #9b6a00; }
.bf-s12-iconstrip .sic.g { background: #e7f4ea; color: #2e7d32; }

.bf-s12-tabstrip { display: flex; background: #f1f5f9; border-bottom: 1px solid #e5e7eb; font-size: 10.5px; flex: 0 0 auto; }
.bf-s12-tab { padding: 3px 12px; border-right: 1px solid #e5e7eb; color: #1f2937; }
.bf-s12-tab.on { background: #fff; }

.bf-s12-substrip { display: flex; align-items: center; gap: 14px; padding: 2px 12px; background: #fff; border-bottom: 1px solid #e5e7eb; font-size: 11px; color: #1976d2; flex: 0 0 auto; }
.bf-s12-substrip .on { border-bottom: 2px solid #1976d2; font-weight: 600; padding-bottom: 1px; }
.bf-s12-substrip > span:not(.on):not(.hm) { color: #4b5563; }
.bf-s12-substrip .hm { margin-left: auto; display: inline-flex; align-items: center; gap: 4px; color: #1f2937; font-weight: 700; }

.bf-s12-body { flex: 1; display: grid; grid-template-columns: 220px minmax(0,1fr) 26px; min-height: 0; overflow: hidden; }
.bf-s12-tree { background: #fff; border-right: 1px solid #e5e7eb; overflow-y: auto; font-size: 10.5px; }
.bf-s12-ti { display: flex; align-items: center; gap: 5px; padding: 2px 6px; }
.bf-s12-ti.on { background: #f8d7da; font-weight: 600; }
.bf-s12-ti.hi { background: #e6f4ea; }
.bf-s12-ti.on.hi { background: #f8d7da; }

.bf-s12-main { background: #fff; padding: 6px 10px; overflow-y: auto; min-height: 0; font-size: 11px; }
.bf-s12-card { border: 1px solid #e5e7eb; border-radius: 3px; margin-bottom: 8px; background: #fff; }
.bf-s12-card-h { display: flex; align-items: center; gap: 5px; padding: 4px 9px; border-bottom: 1px solid #e5e7eb; background: #f1f5f9; font-weight: 600; color: #9b59b6; font-size: 10.5px; }
.bf-s12-card-h .dt { margin-left: auto; color: #4b5563; font-weight: 400; font-size: 10px; }
.bf-s12-card-b { padding: 6px 9px; max-height: 230px; overflow-y: auto; }

.bf-s12-section { margin-bottom: 7px; }
.bf-s12-sh { font-weight: 600; text-decoration: underline; display: flex; align-items: center; gap: 4px; font-size: 11px; }
.bf-s12-sh.red { color: #b3261e; }
.bf-s12-sh.blue { color: #1976d2; }
.bf-s12-sh.gold { color: #9b6a00; }
.bf-s12-sb { padding-left: 14px; font-size: 10.5px; line-height: 1.5; }
.bf-s12-sb.red { color: #b3261e; }
.bf-s12-sb .lk { color: #1976d2; text-decoration: underline; margin-left: 6px; cursor: default; }
.bf-s12-alert { display: flex; align-items: flex-start; gap: 4px; padding: 1px 0; }
.bf-s12-alert .sym { display: inline-block; width: 13px; text-align: center; font-weight: 700; }

.bf-s12-iconcol { border-left: 1px solid #e5e7eb; background: #f8fafc; display: flex; flex-direction: column; align-items: center; padding: 7px 0; gap: 10px; }
.bf-s12-iconcol .bn { font-size: 10px; font-weight: 700; color: #1976d2; }
.bf-s12-iconcol .bn.r { color: #b3261e; }

.bf-s12-foot { display: flex; align-items: center; justify-content: space-between; padding: 1px 8px; background: #f1f5f9; border-top: 1px solid #e5e7eb; font-size: 10.5px; color: #4b5563; flex: 0 0 auto; }
.bf-s12-foot .search { border: 1px solid #cbd5e1; padding: 0 6px; font-size: 10.5px; background: #fff; width: 130px; height: 16px; }
.bf-s12-foot .counts { display: flex; gap: 8px; align-items: center; }

/* Compact BloodFlow note inside SystmOne */
.bf-s12 .bfn { font-size: 10px; }
.bf-s12 .bfn * { min-width: 0; }
.bf-s12 .bfn-banner { padding: 7px 9px; border-radius: 3px; gap: 8px; }
.bf-s12 .bfn-logo { width: 16px; height: 16px; }
.bf-s12 .bfn-banner-title { font-size: 10.5px; }
.bf-s12 .bfn-banner-sub { font-size: 8px; }
.bf-s12 .bfn-chip { display: none; }
.bf-s12 .bfn-meta { grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 6px 10px; padding: 6px 0; margin-top: 6px; }
.bf-s12 .bfn-meta .k { font-size: 7px; }
.bf-s12 .bfn-meta .v { font-size: 9.5px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.bf-s12 .bfn-sec h6 { font-size: 8px; }
.bf-s12 .bfn-row { grid-template-columns: 130px minmax(0, 1fr); gap: 10px; padding: 1px 0; font-size: 9.5px; }
.bf-s12 .bfn-row .lbl { font-size: 8.5px; }
.bf-s12 .bfn-p { font-size: 10px; line-height: 1.4; }
.bf-s12 .bfn-ul li { font-size: 9.5px; line-height: 1.4; padding: 2px 0 2px 12px; }
.bf-s12 .bfn-foot { font-size: 7.5px; padding-top: 6px; }
.bf-s12 .bf-cite { font-size: 7px; padding: 0 3px; }
`;

/* ============ SCLÍNICO (SPMS) — Java Swing classic PT ============ */
function SClinicoShell({ noteSlot }) {
  const consultas = [
    { d: '14-10-2025', p: true },
    { d: '30-08-2024', sel: true, c: true },
    { d: '05-06-2024' },
    { d: '28-06-2023' },
    { d: '13-04-2022', p: true },
  ];
  const tools = [
    { I: LogOut,        c: '#1d4f7a', l: 'Sair' },
    { I: FileText,      c: '#1d4f7a', l: 'Salvar' },
    { I: Users,         c: '#1d4f7a', l: 'Antec.' },
    { I: Printer,       c: '#1d4f7a', l: 'Impres.' },
    { I: Lock,          c: '#1d4f7a', l: 'Confid.' },
    { I: BookOpen,      c: '#1d4f7a', l: 'Diários' },
  ];
  return (
    <div className="bf-sc2">
      <div className="bf-sc2-title">
        <div className="bf-sc2-title-l">
          <span className="bf-sc2-ic">S</span>
          <span>Diário Clínico — Pediatria</span>
        </div>
        <div className="bf-sc2-wb">
          <span>_</span><span>▢</span><span className="x">✕</span>
        </div>
      </div>
      <div className="bf-sc2-toolbar">
        <div className="bf-sc2-tools">
          {tools.map((t, i) => (
            <button key={i} className="bf-sc2-tb">
              <span className="ic"><t.I size={16} color={t.c}/></span>
              <span>{t.l}</span>
            </button>
          ))}
        </div>
        <div className="bf-sc2-toolinfo">
          <div><b>Alta da consulta:</b> Sim</div>
          <div><b>Médico(a) da consulta:</b> Dr(a). Margarida Marujo</div>
        </div>
        <div className="bf-sc2-doente">
          <div className="l">Doente</div>
          <div className="v">Jose Miguel Tome Colaco (9 anos)</div>
        </div>
      </div>
      <div className="bf-sc2-flex1">
        <div className="bf-sc2-body">
          {/* Left: Consultas list */}
          <div className="bf-sc2-side">
            <div className="bf-sc2-side-h">Consultas</div>
            <div className="bf-sc2-th">
              <span>Data</span><span>P</span><span>C</span>
            </div>
            <div className="bf-sc2-list">
              {consultas.map((r, i) => (
                <div key={i} className={'bf-sc2-row' + (r.sel ? ' sel' : '')}>
                  <span className="dt">
                    <span className={'rd' + (r.sel ? ' on' : '')}></span>
                    {r.d}
                  </span>
                  <span className={r.p ? 'cell pp' : 'cell'}>{r.p ? 'P' : ''}</span>
                  <span className={r.c ? 'cell cc' : 'cell'}></span>
                </div>
              ))}
              {Array.from({ length: 9 }).map((_, i) => (
                <div key={'e' + i} className="bf-sc2-row empty">
                  <span className="dt"><span className="rd"></span></span>
                  <span className="cell"></span>
                  <span className="cell"></span>
                </div>
              ))}
            </div>
            <div className="bf-sc2-side-foot">
              <div className="actions">
                <button>+</button>
                <button>›</button>
              </div>
              <div className="bf-sc2-th2">Textos pré-definidos</div>
              <div className="bf-sc2-th"><span style={{ gridColumn: '1 / -1' }}>Descrição</span></div>
              <div className="bf-sc2-obs">Observação</div>
            </div>
          </div>

          {/* Center: Diary editor */}
          <div className="bf-sc2-center">
            <div className="bf-sc2-center-h">
              <span><b>Médico(a) do diário:</b> Dr(a). Margarida Marujo</span>
              <button className="bf-sc2-plus">+</button>
            </div>
            <div className="bf-sc2-diary" data-bf-scroll>
              {noteSlot}
            </div>
          </div>

          {/* Right: Date browser + history */}
          <div className="bf-sc2-right">
            <div className="bf-sc2-right-h">
              <select defaultValue="JAN">
                {['JAN','FEV','MAR','ABR','MAI','JUN','JUL','AGO','SET','OUT','NOV','DEZ'].map(m => <option key={m}>{m}</option>)}
              </select>
              <button>‹</button>
              <span>2026</span>
              <button>›</button>
            </div>
            <div className="bf-sc2-hist" data-bf-scroll>
              <div className="entry">
                <div className="au"><b>Dr(a). Isabel Santa Marta</b> · 13-01-2026 12:16</div>
                <div>P&nbsp;&nbsp;&nbsp;&nbsp;C-Alt-&nbsp;&nbsp;&nbsp;&nbsp;FC.</div>
                <div>TA</div>
                <div>Desenvolvimento psíquico</div>
                <br/>
                <div>Observ.</div>
                <br/>
                <div>E.N.</div>
                <div>Orientação :</div>
                <div className="mut">"</div>
                <br/>
                <div className="mut">— sem queixas agudas —</div>
                <div className="au"><b>Dr(a). Isabel Santa Marta</b> · 13-01-2026 12:16</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{BF_SC2_CSS}</style>
    </div>
  );
}

const BF_SC2_CSS = `
.bf-sc2 { position: absolute; inset: 0; display: flex; flex-direction: column; background: #ebedef; color: #1f1f1f; font-family: Tahoma, 'Segoe UI', sans-serif; font-size: 11px; }
.bf-sc2 * { box-sizing: border-box; min-width: 0; }

/* Title bar — grey gradient like Java Swing */
.bf-sc2-title { display: flex; align-items: center; justify-content: space-between; padding: 3px 8px; color: #fff; background: linear-gradient(180deg, #6e7e8c 0%, #3f4b54 100%); flex: 0 0 auto; }
.bf-sc2-title-l { display: flex; align-items: center; gap: 6px; font-size: 11.5px; }
.bf-sc2-ic { width: 14px; height: 14px; background: #1d4f7a; color: #fff; display: grid; place-items: center; font-size: 9px; font-weight: 700; }
.bf-sc2-wb { display: flex; gap: 0; }
.bf-sc2-wb span { padding: 1px 8px; cursor: default; }
.bf-sc2-wb .x:hover { background: #c0392b; }

/* Top toolbar — Swing classic with bordered icon buttons */
.bf-sc2-toolbar { display: flex; align-items: flex-start; justify-content: space-between; padding: 5px 8px; background: #dde1e4; border-bottom: 1px solid #a8b1ba; gap: 14px; flex: 0 0 auto; }
.bf-sc2-tools { display: flex; gap: 1px; }
.bf-sc2-tb { display: flex; flex-direction: column; align-items: center; gap: 1px; padding: 2px 6px; background: transparent; border: 1px solid transparent; cursor: default; font-size: 9.5px; color: #1f1f1f; }
.bf-sc2-tb:hover { background: rgba(255,255,255,0.4); border-color: #a8b1ba; }
.bf-sc2-tb .ic { padding: 3px; background: #fff; border: 1px solid #a8b1ba; border-radius: 1px; display: grid; place-items: center; }
.bf-sc2-toolinfo { font-size: 10.5px; line-height: 1.45; padding: 4px 0; }
.bf-sc2-doente { background: #f3a86a; color: #1f1f1f; padding: 4px 10px; text-align: right; border-radius: 1px; min-width: 200px; }
.bf-sc2-doente .l { font-size: 10px; font-weight: 700; }
.bf-sc2-doente .v { font-size: 11px; font-weight: 700; }

.bf-sc2-flex1 { flex: 1; display: flex; min-height: 0; overflow: hidden; }
.bf-sc2-body { flex: 1; display: grid; grid-template-columns: 220px minmax(0,1fr) 290px; min-height: 0; overflow: hidden; }

/* Side */
.bf-sc2-side { display: flex; flex-direction: column; min-height: 0; border-right: 1px solid #a8b1ba; background: #fff; }
.bf-sc2-side-h { padding: 3px 8px; font-weight: 600; background: #dde1e4; border-bottom: 1px solid #a8b1ba; font-size: 10.5px; flex: 0 0 auto; }
.bf-sc2-th { display: grid; grid-template-columns: 1fr 28px 28px; background: #cdd4d9; border-bottom: 1px solid #a8b1ba; font-size: 10px; font-weight: 600; flex: 0 0 auto; }
.bf-sc2-th > span { padding: 2px 6px; border-right: 1px solid #a8b1ba; }
.bf-sc2-th > span:last-child { border-right: none; }
.bf-sc2-th2 { padding: 3px 8px; font-weight: 600; font-size: 10.5px; border-top: 1px solid #a8b1ba; background: #dde1e4; }
.bf-sc2-list { flex: 1; overflow-y: auto; background: #fff; }
.bf-sc2-row { display: grid; grid-template-columns: 1fr 28px 28px; font-size: 10.5px; border-bottom: 1px solid #cfd4d8; cursor: default; }
.bf-sc2-row .dt { padding: 2px 6px; display: flex; align-items: center; gap: 5px; height: 18px; border-right: 1px solid #cfd4d8; }
.bf-sc2-row .cell { display: grid; place-items: center; border-right: 1px solid #cfd4d8; }
.bf-sc2-row .cell:last-child { border-right: none; }
.bf-sc2-row .cell.pp { background: #fff4ad; color: #9b6a00; font-weight: 700; }
.bf-sc2-row .cell.cc { background: #41b3df; }
.bf-sc2-row.empty { height: 22px; }
.bf-sc2-row:hover:not(.sel):not(.empty) { background: #eef2f5; }
.bf-sc2-row.sel { background: #cfe2f3; }
.bf-sc2-row .rd { width: 8px; height: 8px; border-radius: 50%; border: 1px solid #7a8590; background: #fff; flex: 0 0 auto; }
.bf-sc2-row .rd.on { background: #1d4f7a; border-color: #1d4f7a; }
.bf-sc2-side-foot { background: #fff; flex: 0 0 auto; }
.bf-sc2-side-foot .actions { display: flex; justify-content: flex-end; gap: 4px; padding: 4px 8px; background: #dde1e4; border-top: 1px solid #a8b1ba; }
.bf-sc2-side-foot button { background: #fff; border: 1px solid #a8b1ba; width: 18px; height: 18px; cursor: default; font-size: 11px; color: #1d4f7a; }
.bf-sc2-obs { background: #fff; padding: 2px 8px; font-size: 10.5px; border-top: 1px solid #cfd4d8; }

/* Center */
.bf-sc2-center { display: flex; flex-direction: column; min-height: 0; border-right: 1px solid #a8b1ba; }
.bf-sc2-center-h { display: flex; align-items: center; justify-content: space-between; padding: 3px 8px; font-size: 10.5px; background: #eef2f5; border-bottom: 1px solid #a8b1ba; flex: 0 0 auto; }
.bf-sc2-plus { background: transparent; border: none; color: #1976d2; font-size: 14px; cursor: default; padding: 0 4px; }
.bf-sc2-diary { flex: 1; min-height: 0; overflow-y: auto; padding: 10px 14px; background: #fff; font-size: 11px; }

/* Right */
.bf-sc2-right { display: flex; flex-direction: column; min-height: 0; background: #fff; }
.bf-sc2-right-h { display: flex; align-items: center; gap: 4px; padding: 4px 6px; background: #eef2f5; border-bottom: 1px solid #a8b1ba; flex: 0 0 auto; }
.bf-sc2-right-h select { font-size: 10px; border: 1px solid #a8b1ba; background: #fff; padding: 1px 3px; flex: 1; }
.bf-sc2-right-h button { background: #fff; border: 1px solid #a8b1ba; width: 18px; height: 18px; font-size: 10px; cursor: default; }
.bf-sc2-right-h > span { font-size: 10.5px; }
.bf-sc2-hist { flex: 1; min-height: 0; overflow-y: auto; padding: 6px 8px; font-size: 10.5px; color: #2a2a2a; line-height: 1.55; }
.bf-sc2-hist .au { color: #1f6fb5; font-weight: 700; }
.bf-sc2-hist .mut { color: #888; }

/* Compact BloodFlow note */
.bf-sc2 .bfn { font-size: 10px; }
.bf-sc2 .bfn * { min-width: 0; }
.bf-sc2 .bfn-banner { padding: 7px 9px; border-radius: 3px; gap: 8px; }
.bf-sc2 .bfn-logo { width: 16px; height: 16px; }
.bf-sc2 .bfn-banner-title { font-size: 10.5px; }
.bf-sc2 .bfn-banner-sub { font-size: 8px; }
.bf-sc2 .bfn-chip { display: none; }
.bf-sc2 .bfn-meta { grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 6px 10px; padding: 6px 0; margin-top: 6px; }
.bf-sc2 .bfn-meta .k { font-size: 7px; }
.bf-sc2 .bfn-meta .v { font-size: 9.5px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.bf-sc2 .bfn-sec h6 { font-size: 8px; }
.bf-sc2 .bfn-row { grid-template-columns: 130px minmax(0, 1fr); gap: 10px; padding: 1px 0; font-size: 9.5px; }
.bf-sc2 .bfn-row .lbl { font-size: 8.5px; }
.bf-sc2 .bfn-p { font-size: 10px; line-height: 1.4; }
.bf-sc2 .bfn-ul li { font-size: 9.5px; line-height: 1.4; padding: 2px 0 2px 12px; }
.bf-sc2 .bfn-foot { font-size: 7.5px; padding-top: 6px; }
.bf-sc2 .bf-cite { font-size: 7px; padding: 0 3px; }
`;

/* ============ Generic placeholder ============ */
function PlaceholderShell({ noteSlot, emr }) {
  return (
    <div className="ph-shell">
      <div className="ph-top">
        <span style={{ width: 18, height: 18, borderRadius: 4, background: emr.markBg, display: 'grid', placeItems: 'center', fontSize: 9, fontWeight: 700 }}>{emr.mark}</span>
        {emr.name} · {emr.vendor}
        <span style={{ marginLeft: 'auto', opacity: 0.6, fontSize: 11 }}>chrome in progress</span>
      </div>
      <div className="ph-main">
        <div className="ph-left">Patient chart · {emr.vendor}</div>
        <div className="ph-right" data-bf-scroll>{noteSlot}</div>
      </div>
    </div>
  );
}

export const SHELLS = {
  epic: EpicShell,
  cerner: CernerShell,
  glintt: GlinttShell,
  trakcare: TrakCareShell,
  orbis: OrbisShell,
  emis: EmisShell,
  systmone: SystmOneShell,
  sclinico: SClinicoShell,
  _placeholder: PlaceholderShell,
};
